// Clients.jsx - Client Management & Financial Dashboard
// v4.2 - Financial tiles, CRM, notifications, analytics

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Plus, Minus, Search, Filter, MoreVertical, Edit, Trash2, Eye,
  DollarSign, Bot, Server, Clock, AlertCircle, CheckCircle,
  RefreshCw, X, Users, Building2, Mail, Phone, CreditCard,
  Calendar, UserPlus, AlertTriangle, Pause, Play, ChevronRight,
  FileText, Download, ChevronDown, ChevronUp, Receipt, Upload,
  Bell, MessageCircle, TrendingUp, TrendingDown, ArrowUpRight,
  Target, Activity, Send, ExternalLink, XCircle, StickyNote,
  Banknote, ArrowRightLeft, ShieldAlert, BarChart3,
  Loader2, Music, Volume2
} from 'lucide-react'
import { clientAdminApi, dialerApi, resellerAdminApi } from '../services/api'
import SearchableDropdown from '../components/SearchableDropdown'
import InvoiceEditor from '../components/InvoiceEditor'
import ClientBrandingFields, { brandingDefaults, pickBranding, uploadPendingBranding } from '../components/ClientBrandingFields'
import { useAuth } from '../context/AuthContext'
import './Clients.css'

// Small read-only preview of what the auto-generation cron will produce
// next for this client, based on the client's last invoice's period_end
// + total_days. Shown inside the Edit Client modal so admins can SEE the
// continuous cycle is already wired up and they don't need to touch any
// date field themselves.
function NextCyclePreview({ clientId }) {
  const [info, setInfo] = useState(null)
  const [err, setErr] = useState(null)
  useEffect(() => {
    if (!clientId) return
    let cancelled = false
    clientAdminApi.getNextCycle
      ? clientAdminApi.getNextCycle(clientId).then(r => { if (!cancelled) setInfo(r.data) }).catch(e => { if (!cancelled) setErr(e.message) })
      : fetch(`/api/client-admin/invoices/next-cycle/${clientId}`)
          .then(r => r.json()).then(d => { if (!cancelled) setInfo(d) }).catch(e => { if (!cancelled) setErr(e.message) })
    return () => { cancelled = true }
  }, [clientId])
  if (!clientId) return null
  if (err || !info) return null
  if (!info.success) return null
  const fmt = (s) => { try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) } catch { return s } }
  return (
    <div style={{
      marginTop: '8px',
      padding: '12px 14px',
      background: 'rgba(34, 197, 94, 0.08)',
      border: '1px solid rgba(34, 197, 94, 0.25)',
      borderRadius: 'var(--radius-md)',
      fontSize: '0.8rem',
      color: 'var(--text-primary)',
      lineHeight: 1.5
    }}>
      <div style={{ fontWeight: 600, marginBottom: '4px', color: 'var(--success, #16a34a)' }}>
        Next auto-generated invoice
      </div>
      <div>Period: <strong>{fmt(info.period_start)}</strong> → <strong>{fmt(info.period_end)}</strong></div>
      <div>Issue date: <strong>{fmt(info.issue_date)}</strong></div>
      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '4px' }}>
        Derived from {info.source === 'last_invoice' ? 'last invoice — cycle carries forward automatically' : 'Initial Invoice Day (no prior invoices yet)'}
      </div>
    </div>
  )
}

function Clients() {
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()

  // Tab state
  const [activeTab, setActiveTab] = useState('list') // list, invoices, sheet
  const [botChangesLog, setBotChangesLog] = useState([])
  const [botChangesLoading, setBotChangesLoading] = useState(false)
  const [sheetMonth, setSheetMonth] = useState(new Date().toISOString().slice(0, 7)) // YYYY-MM
  const [editingChange, setEditingChange] = useState(null) // inline edit row
  const [showAddRow, setShowAddRow] = useState(false)
  const [newRow, setNewRow] = useState({ client_id: '', change_type: 'addition', bot_count: '', effective_date: new Date().toISOString().split('T')[0], notes: '' })

  // Data states
  const [clients, setClients] = useState([])
  const [availableDialers, setAvailableDialers] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  // Financial stats
  const [financialStats, setFinancialStats] = useState({})
  const [financialClients, setFinancialClients] = useState([])
  const [selectedMonth, setSelectedMonth] = useState('') // '' = all time
  const [activeTileFilter, setActiveTileFilter] = useState(null) // tile filter for client list

  // Notification sidebar
  const [notifications, setNotifications] = useState({ due_count: 0, expected_this_week: [] })
  const [showNotifications, setShowNotifications] = useState(false)
  const [showFilters, setShowFilters] = useState(false)

  // Filter states
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')

  // Modal states
  const [showModal, setShowModal] = useState(false)
  const [pendingBranding, setPendingBranding] = useState({})   // logo / logo_dark / favicon: File | 'remove'
  const [resellers, setResellers] = useState([])               // for the "Reseller" picker (v5.0)
  const [brandingError, setBrandingError] = useState(null)
  const [modalMode, setModalMode] = useState('add')
  const [currentClient, setCurrentClient] = useState(null)
  const [activeMenu, setActiveMenu] = useState(null)

  // Invoice states
  const [invoices, setInvoices] = useState([])
  const [invoiceStats, setInvoiceStats] = useState({})
  const [invoiceLoading, setInvoiceLoading] = useState(false)
  const [invoiceSearch, setInvoiceSearch] = useState('')
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('all')
  const [invoiceClientFilter, setInvoiceClientFilter] = useState('all')
  const [viewInvoice, setViewInvoice] = useState(null) // invoice detail popup
  const [invoicePayments, setInvoicePayments] = useState([]) // payments for viewed invoice
  const [editingPayment, setEditingPayment] = useState(null) // payment being edited inline
  const [editingLineItems, setEditingLineItems] = useState(false) // toggle line item edit mode
  const [invoiceSection, setInvoiceSection] = useState('active') // active | pending_approval
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0)
  const [approvingId, setApprovingId] = useState(null)
  // Multi-select for clients list
  const [selectedClientIds, setSelectedClientIds] = useState(() => new Set())
  const [expandedInvoice, setExpandedInvoice] = useState(null)
  const [currentInvoice, setCurrentInvoice] = useState(null)
  const [paidAmount, setPaidAmount] = useState('')
  const [paidReference, setPaidReference] = useState('')
  const [paymentCurrency, setPaymentCurrency] = useState('USD')
  const [paymentAmountOriginal, setPaymentAmountOriginal] = useState('')
  const [paymentExchangeRate, setPaymentExchangeRate] = useState('')
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0])
  const [generateClientId, setGenerateClientId] = useState('')
  const [generatePeriodStart, setGeneratePeriodStart] = useState('')
  const [generatePeriodEnd, setGeneratePeriodEnd] = useState('')
  const [generateIssueDate, setGenerateIssueDate] = useState('')
  const [generateExchangeRate, setGenerateExchangeRate] = useState('280')
  const [editInvoiceData, setEditInvoiceData] = useState({})
  const [lineItemData, setLineItemData] = useState({})
  const [downloading, setDownloading] = useState(null)
  const [showInvoiceEditor, setShowInvoiceEditor] = useState(false)
  const [editingInvoice, setEditingInvoice] = useState(null)

  // CRM / Notes states
  const [showNotesModal, setShowNotesModal] = useState(false)
  const [notesClient, setNotesClient] = useState(null)
  const [clientNotes, setClientNotes] = useState([])
  const [newNoteContent, setNewNoteContent] = useState('')
  const [newNoteType, setNewNoteType] = useState('note')
  const [notesLoading, setNotesLoading] = useState(false)

  // Bad debt modal
  const [showBadDebtModal, setShowBadDebtModal] = useState(false)
  const [badDebtInvoice, setBadDebtInvoice] = useState(null)
  const [badDebtReason, setBadDebtReason] = useState('')

  // Subscription modal
  const [subClient, setSubClient] = useState(null)
  const [subAddDays, setSubAddDays] = useState('')
  const [subBusy, setSubBusy] = useState(false)

  // Sample Voices picker modal
  const [svClient, setSvClient] = useState(null)          // client whose picker is open
  const [svVoiceSets, setSvVoiceSets] = useState([])      // [{id,name,gender}]
  const [svSelectedVsId, setSvSelectedVsId] = useState('') // currently chosen voice set
  const [svClips, setSvClips] = useState([])              // clips of chosen voice set
  const [svSelection, setSvSelection] = useState([])      // working list of selected voices
  const [svClipsLoading, setSvClipsLoading] = useState(false)
  const [svBusy, setSvBusy] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    name: '', contact_person: '', phone: '', address: '', server_ip: '',
    username: '', password: '', status: 'active', balance: 0,
    default_bot_price: 15.00, invoice_day: 1, notes: '',
    whatsapp_number: '', follow_up_date: '', risk_status: 'low',
    target_bots: 0, payment_terms_days: 3
  })

  // Credit form state
  const [creditAmount, setCreditAmount] = useState('')
  const [creditDescription, setCreditDescription] = useState('')

  // Assign dialer state
  const [selectedDialer, setSelectedDialer] = useState('')
  const [customBotPrice, setCustomBotPrice] = useState('')

  // Toast state
  const [toast, setToast] = useState(null)

  useEffect(() => { loadData() }, [])

  // Load payments when viewing an invoice
  useEffect(() => {
    if (viewInvoice?.id) {
      clientAdminApi.getInvoicePayments(viewInvoice.id).then(res => {
        setInvoicePayments(res.data?.payments || [])
      }).catch(() => setInvoicePayments([]))
    } else {
      setInvoicePayments([])
      setEditingLineItems(false)
      setEditingPayment(null)
    }
  }, [viewInvoice?.id])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveMenu(null)
    if (activeMenu) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [activeMenu])

  // Close notification panel on any outside click WITHOUT a full-page
  // click-catcher overlay — so other toolbar buttons stay clickable
  // while the panel is open (first click closes the panel AND lands on
  // the target button).
  useEffect(() => {
    if (!showNotifications) return
    const handler = (e) => {
      if (e.target.closest('.notification-bell-wrapper')) return
      setShowNotifications(false)
    }
    // Defer one tick so the click that opened the panel isn't captured.
    const timer = setTimeout(() => document.addEventListener('click', handler), 0)
    return () => { clearTimeout(timer); document.removeEventListener('click', handler) }
  }, [showNotifications])

  const loadData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true)
    else setLoading(true)

    try {
      const [clientsRes, dialersRes, notifRes] = await Promise.all([
        clientAdminApi.getClients(),
        clientAdminApi.getAvailableDialers(),
        clientAdminApi.getNotifications()
      ])

      if (clientsRes.data?.success) setClients(clientsRes.data.clients || [])
      if (dialersRes.data?.success) setAvailableDialers(dialersRes.data.dialers || [])
      if (notifRes.data?.success) setNotifications(notifRes.data)

      // Load financial stats
      loadFinancialStats()
    } catch (error) {
      console.error('Failed to load data:', error)
      showToast('Failed to load clients', 'error')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const loadFinancialStats = async () => {
    try {
      const res = await clientAdminApi.getFinancialStats(selectedMonth || undefined)
      if (res.data?.success) {
        setFinancialStats(res.data.stats || {})
        setFinancialClients(res.data.clients || [])
      }
    } catch (error) {
      console.error('Failed to load financial stats:', error)
    }
  }

  useEffect(() => { loadFinancialStats() }, [selectedMonth])

  const loadInvoices = async () => {
    setInvoiceLoading(true)
    try {
      const params = { section: invoiceSection }
      if (invoiceStatusFilter !== 'all') params.status = invoiceStatusFilter
      if (invoiceClientFilter !== 'all') params.client_id = invoiceClientFilter
      if (selectedMonth) {
        params.from_date = `${selectedMonth}-01`
        const [y, m] = selectedMonth.split('-').map(Number)
        const lastDay = new Date(y, m, 0).getDate()
        params.to_date = `${selectedMonth}-${String(lastDay).padStart(2, '0')}`
      }

      const res = await clientAdminApi.getInvoices(params)
      if (res.data?.success) {
        setInvoices(res.data.invoices || [])
        setInvoiceStats(res.data.stats || {})
      }
    } catch (error) {
      console.error('Failed to load invoices:', error)
      showToast('Failed to load invoices', 'error')
    } finally {
      setInvoiceLoading(false)
    }
  }

  // Pending-approval badge count (refreshed alongside the main list)
  const loadPendingApprovalCount = async () => {
    try {
      const res = await clientAdminApi.getInvoices({ section: 'pending_approval' })
      if (res.data?.success) setPendingApprovalCount((res.data.invoices || []).length)
    } catch { /* ignore */ }
  }

  // Load invoices when switching to invoices tab / section / filters
  useEffect(() => {
    if (activeTab === 'invoices') {
      loadInvoices()
      loadPendingApprovalCount()
    }
  }, [activeTab, invoiceSection, invoiceStatusFilter, invoiceClientFilter, selectedMonth])

  // Load bot changes log when switching to sheet tab
  const loadBotChangesLog = async () => {
    setBotChangesLoading(true)
    try {
      const from = sheetMonth ? sheetMonth + '-01' : undefined
      const to = sheetMonth ? new Date(parseInt(sheetMonth.split('-')[0]), parseInt(sheetMonth.split('-')[1]), 0).toISOString().split('T')[0] : undefined
      const res = await clientAdminApi.getBotChangesLog(from, to)
      if (res.data?.success) setBotChangesLog(res.data.changes || [])
    } catch (e) { console.error('Failed to load bot changes log:', e) }
    setBotChangesLoading(false)
  }
  useEffect(() => { if (activeTab === 'sheet') loadBotChangesLog() }, [activeTab, sheetMonth])

  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  // ===== Subscription controls =====
  const openSubscription = (client) => {
    setSubClient(client)
    setSubAddDays('')
    setActiveMenu(null)
  }

  // Refresh clients list and keep the open subscription modal in sync
  const refreshSubClient = async () => {
    try {
      const res = await clientAdminApi.getClients()
      if (res.data?.success) {
        const list = res.data.clients || []
        setClients(list)
        setSubClient(prev => prev ? (list.find(c => c.id === prev.id) || prev) : prev)
      }
    } catch (err) {
      showToast('Failed to refresh clients', 'error')
    }
  }

  const handleAddDays = async () => {
    if (!subClient) return
    const days = parseInt(subAddDays, 10)
    if (!days || days <= 0) { showToast('Enter a positive number of days', 'error'); return }
    setSubBusy(true)
    try {
      const res = await clientAdminApi.updateSubscription(subClient.id, { add_days: days })
      if (res.data?.success) {
        showToast(`Added ${days} day${days === 1 ? '' : 's'} to ${subClient.name}`)
        setSubAddDays('')
        await refreshSubClient()
      } else {
        showToast(res.data?.error || 'Failed to add days', 'error')
      }
    } catch (err) {
      showToast('Failed to add days', 'error')
    } finally {
      setSubBusy(false)
    }
  }

  const handleToggleStopOnExpiry = async (checked) => {
    if (!subClient) return
    setSubBusy(true)
    try {
      const res = await clientAdminApi.updateSubscription(subClient.id, { stop_bots_on_expiry: checked })
      if (res.data?.success) {
        showToast(checked ? 'Bots will stop at expiry' : 'Bots will keep running at expiry')
        await refreshSubClient()
      } else {
        showToast(res.data?.error || 'Failed to update setting', 'error')
      }
    } catch (err) {
      showToast('Failed to update setting', 'error')
    } finally {
      setSubBusy(false)
    }
  }

  const handleClientBots = async (action) => {
    if (!subClient) return
    if (action === 'stop' && !window.confirm("Stop all of this client's bots?")) return
    setSubBusy(true)
    try {
      const res = await clientAdminApi.controlClientBots(subClient.id, action)
      if (res.data?.success) {
        showToast(`${action === 'start' ? 'Started' : 'Stopped'} ${res.data.ok}/${res.data.total} bots`)
        await refreshSubClient()
      } else {
        showToast(res.data?.error || `Failed to ${action} bots`, 'error')
      }
    } catch (err) {
      showToast(`Failed to ${action} bots`, 'error')
    } finally {
      setSubBusy(false)
    }
  }

  // ===== Sample Voices picker =====
  // A key uniquely identifying a selected clip (voice set + filename)
  const svKey = (v) => `${v.voice_set_id}::${v.filename}`

  const openSampleVoices = async (client) => {
    setSvClient(client)
    setSvSelectedVsId('')
    setSvClips([])
    setSvSelection([])
    setActiveMenu(null)
    try {
      const [vsRes, selRes] = await Promise.all([
        clientAdminApi.getVoiceSets(),
        clientAdminApi.getClientSampleVoices(client.id)
      ])
      if (vsRes.data?.success) setSvVoiceSets(vsRes.data.voice_sets || [])
      if (selRes.data?.success) {
        // Normalize current selection into the working list shape
        setSvSelection((selRes.data.selected || []).map(s => ({
          voice_set_id: s.voice_set_id,
          voice_set_name: s.voice_set_name,
          category_id: s.category_id,
          category_name: s.category_name,
          filename: s.filename,
          label: s.label || s.category_name,
          audio_url: s.audio_url
        })))
      }
    } catch (err) {
      showToast('Failed to load voice sets', 'error')
    }
  }

  const handleSelectVoiceSet = async (vsId) => {
    setSvSelectedVsId(vsId)
    setSvClips([])
    if (!vsId) return
    setSvClipsLoading(true)
    try {
      const res = await clientAdminApi.getVoiceSetClips(vsId)
      if (res.data?.success) setSvClips(res.data.clips || [])
    } catch (err) {
      showToast('Failed to load clips', 'error')
    } finally {
      setSvClipsLoading(false)
    }
  }

  // Toggle a clip in/out of the working selection
  const toggleSampleClip = (clip) => {
    const vs = svVoiceSets.find(v => String(v.id) === String(svSelectedVsId))
    const item = {
      voice_set_id: Number(svSelectedVsId),
      voice_set_name: vs?.name || '',
      category_id: clip.category_id,
      category_name: clip.category_name,
      filename: clip.filename,
      label: clip.category_name,
      audio_url: clip.audio_url
    }
    setSvSelection(prev => {
      const exists = prev.some(v => svKey(v) === svKey(item))
      if (exists) return prev.filter(v => svKey(v) !== svKey(item))
      return [...prev, item]
    })
  }

  const removeSampleVoice = (item) => {
    setSvSelection(prev => prev.filter(v => svKey(v) !== svKey(item)))
  }

  const handleSaveSampleVoices = async () => {
    if (!svClient) return
    setSvBusy(true)
    try {
      const voices = svSelection.map(v => ({
        voice_set_id: v.voice_set_id,
        category_id: v.category_id,
        filename: v.filename,
        label: v.label || v.category_name
      }))
      const res = await clientAdminApi.setClientSampleVoices(svClient.id, voices)
      if (res.data?.success) {
        showToast(`Saved ${voices.length} sample voice${voices.length === 1 ? '' : 's'}`)
        setSvClient(null)
      } else {
        showToast(res.data?.error || 'Failed to save sample voices', 'error')
      }
    } catch (err) {
      showToast('Failed to save sample voices', 'error')
    } finally {
      setSvBusy(false)
    }
  }

  // Client multi-select helpers
  const toggleClientSelection = (id) => {
    setSelectedClientIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }
  const clearClientSelection = () => setSelectedClientIds(new Set())
  const selectAllVisibleClients = (visible) => setSelectedClientIds(new Set(visible.map(c => c.id)))

  // Bulk actions on selected clients — run sequentially, report counts at the end
  const [bulkBusy, setBulkBusy] = useState(false)
  const runBulkClientAction = async (actionLabel, apiCall) => {
    const ids = Array.from(selectedClientIds)
    if (ids.length === 0) return
    if (!window.confirm(`${actionLabel} ${ids.length} client${ids.length === 1 ? '' : 's'}? This cannot be undone.`)) return
    setBulkBusy(true)
    let ok = 0, failed = 0
    for (const id of ids) {
      try {
        const res = await apiCall(id)
        if (res?.data?.success !== false) ok++; else failed++
      } catch { failed++ }
    }
    setBulkBusy(false)
    clearClientSelection()
    loadData()
    if (failed > 0) showToast(`${actionLabel}: ${ok} ok, ${failed} failed`, 'warning')
    else showToast(`${actionLabel}: ${ok} client${ok === 1 ? '' : 's'}`, 'success')
  }
  const handleBulkSuspend = () => runBulkClientAction('Suspend', (id) => clientAdminApi.deleteClient(id))
  const handleBulkReactivate = () => runBulkClientAction('Reactivate', (id) => clientAdminApi.updateClient(id, { status: 'active' }))

  // Stats calculations
  const stats = useMemo(() => {
    const active = clients.filter(c => c.status === 'active').length
    const suspended = clients.filter(c => c.status === 'suspended').length
    const pending = clients.filter(c => c.status === 'pending').length
    const lowBalance = clients.filter(c => c.status === 'active' && parseFloat(c.balance) <= 50).length
    const totalBots = clients.reduce((sum, c) => sum + (c.total_bots || 0), 0)
    const activeBots = clients.reduce((sum, c) => sum + (c.active_bots || 0), 0)
    const targetBots = clients.reduce((sum, c) => sum + (c.target_bots || 0), 0)
    const totalDialers = clients.reduce((sum, c) => sum + (c.dialer_count || 0), 0)
    const totalServers = new Set(clients.flatMap(c => (Array.isArray(c.servers) ? c.servers : []).map(s => s.ip))).size
    const totalRevenue = clients.reduce((sum, c) => sum + (parseFloat(c.total_revenue) || 0), 0)
    const totalAdditions = clients.reduce((sum, c) => sum + (parseInt(c.total_additions) || 0), 0)
    const totalRemovals = clients.reduce((sum, c) => sum + (parseInt(c.total_removals) || 0), 0)

    return { active, suspended, pending, lowBalance, totalBots, activeBots, targetBots, totalDialers, totalServers, totalRevenue, totalAdditions, totalRemovals, total: clients.length }
  }, [clients])

  // Filter clients based on search, status, and tile filter
  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        if (!client.name?.toLowerCase().includes(query) &&
            !client.company?.toLowerCase().includes(query) &&
            !client.email?.toLowerCase().includes(query) &&
            !client.username?.toLowerCase().includes(query)) {
          return false
        }
      }
      // Status filter
      if (filterStatus !== 'all' && client.status !== filterStatus) return false

      // Tile filter
      if (activeTileFilter) {
        const fc = financialClients.find(fc => fc.id === client.id)
        switch (activeTileFilter) {
          case 'receivable':
            return fc && parseFloat(fc.outstanding) > 0
          case 'pending':
            return fc && parseFloat(fc.outstanding) > 0 && !fc.bad_debt_amount
          case 'bad_debts':
            return fc && parseFloat(fc.bad_debt_amount) > 0
          case 'received':
            return fc && parseFloat(fc.total_paid) > 0
          default:
            break
        }
      }
      return true
    })
  }, [clients, searchQuery, filterStatus, activeTileFilter, financialClients])

  // Modal handlers
  const openModal = (mode, client = null) => {
    setModalMode(mode)
    setCurrentClient(client)
    setActiveMenu(null)

    if (mode === 'add') {
      setFormData({
        name: '', contact_person: '', phone: '', address: '', server_ip: '',
        username: '', password: '', status: 'active', balance: 0,
        default_bot_price: 15.00, invoice_day: 1, notes: '',
        whatsapp_number: '', follow_up_date: '', risk_status: 'low',
        target_bots: 0, payment_terms_days: 3, reseller_id: '',
        ...brandingDefaults()
      })
      setPendingBranding({}); setBrandingError(null)
    } else if (mode === 'edit' && client) {
      setFormData({
        name: client.name || '',
        contact_person: client.contact_person || client.email || '',
        phone: client.phone || '',
        address: client.company || '',
        server_ip: client.server_ip || '',
        username: client.username || '',
        password: '',
        status: client.status || 'active',
        balance: client.balance || 0,
        default_bot_price: client.default_bot_price || 15.00,
        invoice_day: client.invoice_day || 1,
        notes: client.notes || '',
        whatsapp_number: client.whatsapp_number || '',
        follow_up_date: client.follow_up_date?.split('T')[0] || '',
        risk_status: client.risk_status || 'low',
        target_bots: client.target_bots || 0,
        payment_terms_days: client.payment_terms_days || 3,
        // "" = the default reseller (Redstone) — the picker's first option
        reseller_id: client.reseller_id && !client.reseller_is_default ? String(client.reseller_id) : '',
        ...brandingDefaults(client)
      })
      setPendingBranding({}); setBrandingError(null)
    } else if (mode === 'credit') {
      setCreditAmount('')
      setCreditDescription('')
    } else if (mode === 'assign') {
      setSelectedDialer('')
      setCustomBotPrice('')
    }

    if (mode === 'add' || mode === 'edit') {
      resellerAdminApi.getResellers()
        .then(r => setResellers((r.data?.resellers || []).filter(x => x.status !== 'cancelled' || String(x.id) === String(client?.reseller_id))))
        .catch(() => setResellers([]))
    }

    setShowModal(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      if (modalMode === 'add') {
        const createData = {
          name: formData.name, email: formData.contact_person,
          phone: formData.phone, company: formData.address,
          server_ip: formData.server_ip, username: formData.username,
          password: formData.password, status: formData.status,
          balance: formData.balance, default_bot_price: formData.default_bot_price,
          notes: formData.notes, contact_person: formData.contact_person,
          whatsapp_number: formData.whatsapp_number,
          follow_up_date: formData.follow_up_date || null,
          risk_status: formData.risk_status, target_bots: formData.target_bots,
          payment_terms_days: formData.payment_terms_days,
          reseller_id: formData.reseller_id,
          ...pickBranding(formData)
        }
        const res = await clientAdminApi.createClient(createData)
        if (res.data?.success) {
          const imgErr = await uploadPendingBranding(res.data.client_id, pendingBranding)
          const brandErr = res.data.branding_error || imgErr
          showToast(brandErr ? `Client created — branding not saved: ${brandErr}` : 'Client created successfully', brandErr ? 'error' : 'success')
          loadData()
        } else {
          showToast(res.data?.error || 'Failed to create client', 'error')
        }
      } else if (modalMode === 'edit') {
        const updateData = {
          name: formData.name, email: formData.contact_person,
          phone: formData.phone, company: formData.address,
          server_ip: formData.server_ip, status: formData.status,
          default_bot_price: formData.default_bot_price,
          invoice_day: formData.invoice_day, notes: formData.notes,
          contact_person: formData.contact_person,
          whatsapp_number: formData.whatsapp_number,
          follow_up_date: formData.follow_up_date || null,
          risk_status: formData.risk_status, target_bots: formData.target_bots,
          payment_terms_days: formData.payment_terms_days,
          reseller_id: formData.reseller_id,
          ...pickBranding(formData)
        }
        if (formData.password) updateData.password = formData.password

        const res = await clientAdminApi.updateClient(currentClient.id, updateData)
        if (res.data?.success) {
          const imgErr = await uploadPendingBranding(currentClient.id, pendingBranding)
          showToast(imgErr ? `Client updated — image not saved: ${imgErr}` : 'Client updated successfully', imgErr ? 'error' : 'success')
          loadData()
        } else {
          showToast(res.data?.error || 'Failed to update client', 'error')
        }
      } else if (modalMode === 'credit') {
        const res = await clientAdminApi.addCredit(
          currentClient.id, parseFloat(creditAmount), creditDescription,
          currentUser?.id || null, currentUser?.username || null
        )
        if (res.data?.success) {
          showToast(`$${creditAmount} added to ${currentClient.name}'s account`)
          loadData()
        } else {
          showToast(res.data?.error || 'Failed to add credit', 'error')
        }
      } else if (modalMode === 'assign') {
        const res = await clientAdminApi.assignDialer(
          currentClient.id, parseInt(selectedDialer),
          customBotPrice ? parseFloat(customBotPrice) : null
        )
        if (res.data?.success) {
          showToast('Dialer assigned successfully')
          loadData()
        } else {
          showToast(res.data?.error || 'Failed to assign dialer', 'error')
        }
      }
    } catch (error) {
      console.error('Form submission error:', error)
      showToast('An error occurred', 'error')
    }

    setShowModal(false)
  }

  const handleDelete = async (client) => {
    const action = window.confirm(`Delete "${client.name}" permanently?\n\nOK = Delete permanently\nCancel = Cancel`)
    if (!action) return
    try {
      const res = await clientAdminApi.deleteClient(client.id, true)
      if (res.data?.success) { showToast('Client deleted permanently'); loadData() }
    } catch (error) {
      showToast('Failed to delete client', 'error')
    }
    setActiveMenu(null)
  }

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
  }

  const formatDate = (date) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const getStatusClass = (status) => {
    switch (status) {
      case 'active': return 'status-active'
      case 'suspended': return 'status-suspended'
      case 'pending': return 'status-pending'
      case 'under_process': return 'status-pending'
      case 'trial_period': return 'status-trial'
      default: return ''
    }
  }

  const formatStatus = (status) => {
    switch (status) {
      case 'active': return 'Active'
      case 'suspended': return 'Suspended'
      case 'under_process': return 'Under Process'
      case 'trial_period': return 'Trial Period'
      case 'pending': return 'Pending'
      default: return status
    }
  }

  const getBalanceClass = (balance) => {
    const bal = parseFloat(balance)
    if (bal <= 0) return 'balance-critical'
    if (bal <= 50) return 'balance-warning'
    return 'balance-good'
  }

  const getRiskClass = (risk) => {
    switch (risk) {
      case 'low': return 'risk-low'
      case 'medium': return 'risk-medium'
      case 'high': return 'risk-high'
      case 'bad_debt': return 'risk-bad-debt'
      default: return ''
    }
  }

  // Invoice helper functions
  const getInvoiceStatusClass = (status, dueDate) => {
    if (status === 'pending' && new Date(dueDate) < new Date()) return 'status-overdue'
    switch (status) {
      case 'paid': return 'status-paid'
      case 'partial': return 'status-partial'
      case 'pending': return 'status-pending'
      case 'cancelled': return 'status-cancelled'
      default: return ''
    }
  }

  const formatInvoiceStatus = (status, dueDate) => {
    if (status === 'pending' && new Date(dueDate) < new Date()) return 'Overdue'
    return status?.charAt(0).toUpperCase() + status?.slice(1)
  }

  const formatPeriod = (start, end) => {
    const s = new Date(start)
    const e = new Date(end)
    return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
  }

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null
    const now = new Date()
    const due = new Date(dueDate)
    const diffTime = due - now
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  // Filtered invoices
  const filteredInvoices = useMemo(() => {
    return invoices.filter(inv => {
      if (invoiceSearch) {
        const q = invoiceSearch.toLowerCase()
        if (!inv.invoice_no?.toLowerCase().includes(q) && !inv.client_name?.toLowerCase().includes(q)) return false
      }
      return true
    })
  }, [invoices, invoiceSearch])

  // Invoice action handlers
  const handleMarkPaid = async (e) => {
    e.preventDefault()
    if (!currentInvoice) return

    try {
      const paidNum = parseFloat(paidAmount) || 0
      const paymentData = {
        amount_usd: paidNum,
        payment_currency: paymentCurrency,
        payment_reference: paidReference,
        payment_method: 'bank_transfer',
        payment_date: paymentDate || new Date().toISOString().split('T')[0],
        _user_id: currentUser?.id || null,
        _user_name: currentUser?.username || null
      }

      if (paymentCurrency === 'PKR' && paymentAmountOriginal) {
        paymentData.amount_original = parseFloat(paymentAmountOriginal)
        paymentData.exchange_rate = parseFloat(paymentExchangeRate) || 280
      }

      const res = await clientAdminApi.recordPayment(currentInvoice.id, paymentData)
      if (res.data?.success) {
        showToast(`Payment of ${formatCurrency(paidNum)} recorded (${res.data.invoice_status})`)
        loadInvoices()
        loadFinancialStats()
        loadData()
      } else {
        showToast(res.data?.error || 'Failed to record payment', 'error')
      }
    } catch (error) {
      showToast('Failed to record payment', 'error')
    }
    setShowModal(false)
  }

  const handleSavePayment = async (paymentId) => {
    if (!editingPayment) return
    try {
      const res = await clientAdminApi.updatePayment(paymentId, editingPayment)
      if (res.data?.success) {
        showToast('Payment updated')
        setEditingPayment(null)
        if (viewInvoice) {
          const pRes = await clientAdminApi.getInvoicePayments(viewInvoice.id)
          setInvoicePayments(pRes.data?.payments || [])
        }
        loadInvoices(); loadFinancialStats(); loadData()
      }
    } catch (error) { showToast('Failed to update payment', 'error') }
  }

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment? This will reverse the balance change.')) return
    try {
      const res = await clientAdminApi.deletePayment(paymentId)
      if (res.data?.success) {
        showToast('Payment deleted')
        if (viewInvoice) {
          const pRes = await clientAdminApi.getInvoicePayments(viewInvoice.id)
          setInvoicePayments(pRes.data?.payments || [])
        }
        loadInvoices()
        loadFinancialStats()
        loadData()
      }
    } catch (error) {
      showToast('Failed to delete payment', 'error')
    }
  }

  // Approve invoice - makes it visible on client portal and moves it out
  // of the "Pending Approval" section into Active Invoices.
  const handleApproveInvoice = async (invoice) => {
    setApprovingId(invoice.id)
    try {
      const res = await clientAdminApi.approveInvoice(invoice.id, currentUser?.id || null, currentUser?.username || null)
      if (res.data?.success) {
        showToast(`Invoice ${invoice.invoice_no} approved`)
        loadInvoices()
        loadPendingApprovalCount()
      } else {
        showToast(res.data?.error || 'Failed to approve invoice', 'error')
      }
    } catch (error) {
      showToast('Failed to approve invoice', 'error')
    } finally {
      setApprovingId(null)
    }
  }

  const handleUnapproveInvoice = async (invoice) => {
    if (!confirm('This will hide the invoice from the client portal. Continue?')) return
    try {
      const res = await clientAdminApi.unapproveInvoice(invoice.id)
      if (res.data?.success) { showToast('Invoice hidden from client portal'); loadInvoices() }
      else showToast(res.data?.error || 'Failed', 'error')
    } catch (error) { showToast('Failed', 'error') }
  }

  const handleGenerateInvoice = async (e) => {
    e.preventDefault()
    if (!generateClientId) { showToast('Please select a client', 'error'); return }
    try {
      const res = await clientAdminApi.generateInvoice({
        client_id: parseInt(generateClientId),
        period_start: generatePeriodStart || undefined,
        period_end: generatePeriodEnd || undefined,
        issue_date: generateIssueDate || undefined,
        exchange_rate: parseFloat(generateExchangeRate) || 280
      })
      if (res.data?.success) { showToast(`Invoice ${res.data.invoice_no} generated`); loadInvoices(); setShowModal(false) }
      else showToast(res.data?.error || 'Failed to generate invoice', 'error')
    } catch (error) { showToast(error.response?.data?.error || 'Failed to generate invoice', 'error') }
  }

  const handleEditInvoice = async (e) => {
    e.preventDefault()
    if (!currentInvoice) return
    try {
      const res = await clientAdminApi.updateInvoice(currentInvoice.id, editInvoiceData)
      if (res.data?.success) { showToast('Invoice updated'); loadInvoices() }
      else showToast(res.data?.error || 'Failed', 'error')
    } catch (error) { showToast('Failed', 'error') }
    setShowModal(false)
  }

  const handleAddLineItem = async (e) => {
    e.preventDefault()
    if (!currentInvoice) return
    try {
      const res = await clientAdminApi.addLineItem(currentInvoice.id, lineItemData)
      if (res.data?.success) { showToast('Line item added'); loadInvoices() }
      else showToast(res.data?.error || 'Failed', 'error')
    } catch (error) { showToast('Failed', 'error') }
    setShowModal(false)
  }

  const handleDeleteLineItem = async (invoiceId, index) => {
    if (!window.confirm('Delete this line item?')) return
    try {
      const res = await clientAdminApi.deleteLineItem(invoiceId, index)
      if (res.data?.success) { showToast('Line item deleted'); loadInvoices() }
      else showToast(res.data?.error || 'Failed', 'error')
    } catch (error) { showToast('Failed', 'error') }
  }

  const exportInvoicesCSV = () => {
    const data = filteredInvoices
    if (!data.length) return showToast('No invoices to export', 'error')

    const headers = ['Invoice #', 'Client', 'Issue Date', 'Due Date', 'Period Start', 'Period End', 'Bots', 'Days', 'Amount (USD)', 'PKR Amount', 'Exchange Rate', 'Status', 'Paid Amount', 'Paid Date', 'Payment Currency', 'PKR Paid', 'Reference', 'Approved', 'Bad Debt']
    const rows = data.map(inv => [
      inv.invoice_no,
      inv.client_name,
      inv.issue_date?.split('T')[0] || '',
      inv.due_date?.split('T')[0] || '',
      inv.period_start?.split('T')[0] || '',
      inv.period_end?.split('T')[0] || '',
      inv.total_bots || 0,
      inv.total_days || 0,
      inv.subtotal_usd || 0,
      inv.total_pkr || 0,
      inv.exchange_rate || 280,
      inv.status,
      inv.paid_amount || 0,
      inv.paid_at?.split('T')[0] || '',
      inv.payment_currency || '',
      inv.payment_amount_original || '',
      inv.paid_reference || '',
      inv.is_approved ? 'Yes' : 'No',
      inv.is_bad_debt ? 'Yes' : 'No'
    ])

    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `invoices_${selectedMonth || 'all'}_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    URL.revokeObjectURL(url)
    showToast(`Exported ${data.length} invoices to CSV`)
  }

  const exportClientsCSV = () => {
    if (!clients.length) return showToast('No clients to export', 'error')

    const headers = ['Client Name', 'Contact', 'Phone', 'WhatsApp', 'Email', 'Company', 'Dialers', 'Active Bots', 'Total Bots', 'Balance', 'Risk', 'Status', 'Created']
    const rows = clients.map(c => [
      c.name,
      c.contact_person || '',
      c.phone || '',
      c.whatsapp_number || '',
      c.email || '',
      c.company || '',
      c.dialer_count || 0,
      c.active_bots || 0,
      c.total_bots || 0,
      c.balance || 0,
      c.risk_status || 'low',
      c.status,
      c.created_at?.split('T')[0] || ''
    ])

    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `clients_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    URL.revokeObjectURL(url)
    showToast(`Exported ${clients.length} clients to CSV`)
  }

  const handleDeleteInvoice = async (invoice) => {
    if (!window.confirm(`Delete invoice ${invoice.invoice_no}? This cannot be undone.`)) return
    try {
      const res = await clientAdminApi.deleteInvoice(invoice.id)
      if (res.data?.success) { showToast('Invoice deleted'); loadInvoices() }
      else showToast(res.data?.error || 'Failed', 'error')
    } catch (error) { showToast('Failed', 'error') }
  }

  const downloadInvoicePdf = async (invoice) => {
    setDownloading(invoice.id)
    try {
      const res = await clientAdminApi.getInvoicePdf(invoice.id)
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${invoice.invoice_no}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) { showToast('Failed to download PDF', 'error') }
    setDownloading(null)
  }

  const openInvoiceModal = (mode, invoice = null) => {
    setModalMode(mode)
    setCurrentInvoice(invoice)

    if (mode === 'markPaid' || mode === 'markPartial') {
      const usdAmount = invoice?.subtotal_usd?.toString() || ''
      const rate = invoice?.exchange_rate || 280
      setPaidAmount(usdAmount)
      setPaidReference('')
      setPaymentDate(new Date().toISOString().split('T')[0])
      setPaymentCurrency('USD')
      setPaymentAmountOriginal(usdAmount ? (parseFloat(usdAmount) * rate).toFixed(2) : '')
      setPaymentExchangeRate(rate.toString())
    } else if (mode === 'generateInvoice') {
      setGenerateClientId('')
      setGeneratePeriodStart('')
      setGeneratePeriodEnd('')
    } else if (mode === 'editInvoice' && invoice) {
      setEditInvoiceData({
        invoice_no: invoice.invoice_no || '',
        issue_date: invoice.issue_date?.split('T')[0] || '',
        due_date: invoice.due_date?.split('T')[0] || '',
        period_start: invoice.period_start?.split('T')[0] || '',
        period_end: invoice.period_end?.split('T')[0] || '',
        notes: invoice.notes || '',
        exchange_rate: invoice.exchange_rate || 280
      })
    } else if (mode === 'addLineItem') {
      setLineItemData({ description: '', campaign: '', qty: 1, rate: 15, days: 30, date_from: '', date_to: '', amount_usd: 15 })
    }

    setShowModal(true)
  }

  // Notes handlers
  const openNotes = async (client) => {
    setNotesClient(client)
    setShowNotesModal(true)
    setNotesLoading(true)
    setNewNoteContent('')
    setNewNoteType('note')
    try {
      const res = await clientAdminApi.getClientNotes(client.id)
      if (res.data?.success) setClientNotes(res.data.notes || [])
    } catch (error) { console.error('Failed to load notes:', error) }
    setNotesLoading(false)
  }

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return
    try {
      const res = await clientAdminApi.addClientNote(notesClient.id, {
        type: newNoteType, content: newNoteContent,
        _user_id: currentUser?.id, _user_name: currentUser?.username
      })
      if (res.data?.success) {
        setNewNoteContent('')
        const notesRes = await clientAdminApi.getClientNotes(notesClient.id)
        if (notesRes.data?.success) setClientNotes(notesRes.data.notes || [])
        showToast('Note added')
      }
    } catch (error) { showToast('Failed to add note', 'error') }
  }

  const handleDeleteNote = async (noteId) => {
    try {
      await clientAdminApi.deleteClientNote(notesClient.id, noteId)
      setClientNotes(prev => prev.filter(n => n.id !== noteId))
    } catch (error) { showToast('Failed to delete note', 'error') }
  }

  // Bad debt handler
  const handleMarkBadDebt = async () => {
    if (!badDebtInvoice) return
    try {
      const res = await clientAdminApi.markBadDebt(badDebtInvoice.id, true, badDebtReason)
      if (res.data?.success) {
        showToast('Marked as bad debt')
        loadInvoices()
        loadFinancialStats()
      }
    } catch (error) { showToast('Failed', 'error') }
    setShowBadDebtModal(false)
    setBadDebtInvoice(null)
    setBadDebtReason('')
  }

  // WhatsApp template
  const getWhatsAppLink = (client, invoice = null) => {
    const phone = (client.whatsapp_number || client.phone || '').replace(/[^0-9+]/g, '')
    if (!phone) return null
    let message = `Hi ${client.contact_person || client.name}`
    if (invoice) {
      message += `, your invoice ${invoice.invoice_no} for ${formatPeriod(invoice.period_start, invoice.period_end)} for the amount of ${formatCurrency(invoice.subtotal_usd)} is due.`
    }
    return `https://wa.me/${phone.replace('+', '')}?text=${encodeURIComponent(message)}`
  }

  // Month options for dropdown
  const monthOptions = useMemo(() => {
    const options = []
    const now = new Date()
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      options.push({ id: val, name: label })
    }
    return options
  }, [])

  const activeFilterCount = [selectedMonth, invoiceStatusFilter !== 'all' ? invoiceStatusFilter : '', invoiceClientFilter !== 'all' ? invoiceClientFilter : ''].filter(Boolean).length

  const clearAllFilters = () => {
    setSelectedMonth('')
    setInvoiceStatusFilter('all')
    setInvoiceClientFilter('all')
  }

  if (loading) {
    return (
      <div className="clients-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading clients...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="clients-page">
      {/* Toast Notification */}
      <AnimatePresence>
        {toast && (
          <motion.div
            className={`toast ${toast.type}`}
            initial={{ opacity: 0, y: -20, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: -20, x: '-50%' }}
          >
            {toast.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Clients</h1>
          <p className="page-subtitle">
            {activeTab === 'list' ? 'Manage client accounts and billing' : 'View and manage client invoices'}
          </p>
        </div>
        <div className="page-header-actions">
          {/* Filters Button - hide on sheet tab */}
          {activeTab !== 'sheet' && (
            <button
              className={`btn btn-secondary ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={16} />
              Filters
              {activeFilterCount > 0 && (
                <span className="filter-count-badge">{activeFilterCount}</span>
              )}
            </button>
          )}

          {/* Notification Bell */}
          <div className="notification-bell-wrapper">
            <button
              className="btn btn-secondary btn-icon notification-bell"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <Bell size={18} />
              {notifications.due_count > 0 && (
                <span className="notif-badge">{notifications.due_count}</span>
              )}
            </button>

            {/* Notification Sidebar Dropdown */}
            <AnimatePresence>
              {showNotifications && (
                <motion.div
                  className="notification-panel"
                  initial={{ opacity: 0, y: 8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.95 }}
                >
                  <div className="notif-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <h3 style={{ margin: 0 }}>Notifications</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className="notif-count">{notifications.due_count} due</span>
                      <button
                        onClick={() => setShowNotifications(false)}
                        aria-label="Close notifications"
                        style={{
                          border: 'none', background: 'transparent', cursor: 'pointer',
                          padding: '4px', display: 'inline-flex', alignItems: 'center',
                          color: 'var(--text-muted)', borderRadius: '4px'
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="notif-section">
                    <h4>Expected This Week</h4>
                    {(notifications.expected_this_week || []).length === 0 ? (
                      <p className="notif-empty">No items expected this week</p>
                    ) : (
                      <div className="notif-list">
                        {notifications.expected_this_week.map((item, idx) => (
                          <div key={idx} className={`notif-item ${item.item_type}`}>
                            <div className="notif-item-icon">
                              {item.item_type === 'invoice_due' ? <FileText size={14} /> : <Calendar size={14} />}
                            </div>
                            <div className="notif-item-content">
                              <span className="notif-item-title">{item.client_name}</span>
                              <span className="notif-item-detail">
                                {item.item_type === 'invoice_due'
                                  ? `${item.invoice_no} - ${formatCurrency(item.subtotal_usd)}`
                                  : 'Follow-up scheduled'
                                }
                              </span>
                              <span className="notif-item-date">{formatDate(item.due_date)}</span>
                            </div>
                            {item.whatsapp_number && (
                              <a
                                href={getWhatsAppLink({ name: item.client_name, whatsapp_number: item.whatsapp_number, phone: item.phone }, item.item_type === 'invoice_due' ? item : null)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="notif-wa-btn"
                                title="Send WhatsApp"
                              >
                                <MessageCircle size={14} />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button
            onClick={() => activeTab === 'list' ? loadData(true) : loadInvoices()}
            className="btn btn-secondary btn-icon"
            disabled={refreshing || invoiceLoading}
          >
            <RefreshCw size={18} className={(refreshing || invoiceLoading) ? 'spinner' : ''} />
          </button>
          <button className="btn btn-secondary" onClick={activeTab === 'list' ? exportClientsCSV : exportInvoicesCSV} title="Export to CSV">
            <Download size={18} /> CSV
          </button>
          <button className="btn btn-secondary" onClick={() => {
            const base = window.location.origin + '/api/client-admin/export'
            const urls = activeTab === 'list'
              ? `Clients:\n=IMPORTDATA("${base}/clients.csv")`
              : `Invoices:\n=IMPORTDATA("${base}/invoices.csv${selectedMonth ? '?month=' + selectedMonth : ''}")\n\nPayments:\n=IMPORTDATA("${base}/payments.csv")`
            navigator.clipboard.writeText(activeTab === 'list'
              ? `=IMPORTDATA("${base}/clients.csv")`
              : `=IMPORTDATA("${base}/invoices.csv${selectedMonth ? '?month=' + selectedMonth : ''}")`)
            showToast('Google Sheets formula copied! Paste in cell A1')
            alert('Paste this in Google Sheets cell A1:\n\n' + urls)
          }} title="Copy Google Sheets formula">
            <ExternalLink size={18} /> Sheets
          </button>
          {activeTab === 'list' ? (
            <button className="btn btn-primary" onClick={() => openModal('add')}>
              <UserPlus size={18} /> New Client
            </button>
          ) : (
            <button className="btn btn-primary" onClick={() => openInvoiceModal('generateInvoice')}>
              <Receipt size={18} /> Generate Invoice
            </button>
          )}
        </div>
      </div>

      {/* ===== FINANCIAL DASHBOARD TILES ===== */}
      <div className="financial-tiles">
        <div
          className={`fin-tile ${activeTileFilter === null ? 'active' : ''}`}
          onClick={() => setActiveTileFilter(null)}
        >
          <div className="fin-tile-icon green"><DollarSign size={20} /></div>
          <div className="fin-tile-content">
            <span className="fin-tile-value">{formatCurrency(financialStats.total_revenue)}</span>
            <span className="fin-tile-label">Total Revenue</span>
            <span className="fin-tile-sub">{financialStats.total_invoices || 0} invoices</span>
          </div>
        </div>
        <div
          className={`fin-tile ${activeTileFilter === 'received' ? 'active' : ''}`}
          onClick={() => setActiveTileFilter(activeTileFilter === 'received' ? null : 'received')}
        >
          <div className="fin-tile-icon cyan"><CheckCircle size={20} /></div>
          <div className="fin-tile-content">
            <span className="fin-tile-value">{formatCurrency(financialStats.amount_received)}</span>
            <span className="fin-tile-label">Amount Received</span>
            <span className="fin-tile-sub">{(financialStats.paid_count || 0) + (financialStats.partial_count || 0)} paid</span>
          </div>
        </div>
        <div
          className={`fin-tile ${activeTileFilter === 'receivable' ? 'active' : ''}`}
          onClick={() => setActiveTileFilter(activeTileFilter === 'receivable' ? null : 'receivable')}
        >
          <div className="fin-tile-icon orange"><Clock size={20} /></div>
          <div className="fin-tile-content">
            <span className="fin-tile-value">{formatCurrency(financialStats.total_receivable)}</span>
            <span className="fin-tile-label">Total Receivable</span>
            <span className="fin-tile-sub">{financialStats.overdue_count || 0} overdue</span>
          </div>
        </div>
        <div
          className={`fin-tile ${activeTileFilter === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTileFilter(activeTileFilter === 'pending' ? null : 'pending')}
        >
          <div className="fin-tile-icon blue"><AlertCircle size={20} /></div>
          <div className="fin-tile-content">
            <span className="fin-tile-value">{formatCurrency(financialStats.pending_amount)}</span>
            <span className="fin-tile-label">Pending</span>
            <span className="fin-tile-sub">{financialStats.pending_count || 0} invoices</span>
          </div>
        </div>
        <div
          className={`fin-tile ${activeTileFilter === 'bad_debts' ? 'active' : ''}`}
          onClick={() => setActiveTileFilter(activeTileFilter === 'bad_debts' ? null : 'bad_debts')}
        >
          <div className="fin-tile-icon red"><ShieldAlert size={20} /></div>
          <div className="fin-tile-content">
            <span className="fin-tile-value">{formatCurrency(financialStats.bad_debts)}</span>
            <span className="fin-tile-label">Bad Debts</span>
            <span className="fin-tile-sub">{financialStats.bad_debt_count || 0} flagged</span>
          </div>
        </div>
      </div>

      {activeTileFilter && (
        <div className="active-filter-bar">
          <span>Filtered by: <strong>{activeTileFilter.replace('_', ' ')}</strong></span>
          <button onClick={() => setActiveTileFilter(null)}><X size={14} /> Clear</button>
        </div>
      )}

      {/* Section toggle — keeps auto-generated drafts out of the active list */}
      <div style={{ display: 'flex', gap: '8px', margin: '8px 0 12px' }}>
        {[
          { key: 'active', label: 'Active Invoices' },
          { key: 'pending_approval', label: `Pending Approval${pendingApprovalCount > 0 ? ` (${pendingApprovalCount})` : ''}` }
        ].map(s => (
          <button
            key={s.key}
            onClick={() => setInvoiceSection(s.key)}
            style={{
              padding: '8px 16px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid ' + (invoiceSection === s.key ? 'var(--primary)' : 'var(--border-color)'),
              background: invoiceSection === s.key ? 'var(--primary)' : 'transparent',
              color: invoiceSection === s.key ? '#fff' : 'var(--text-primary)',
              fontWeight: 500,
              fontSize: '0.85rem',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {s.label}
            {s.key === 'pending_approval' && pendingApprovalCount > 0 && invoiceSection !== s.key && (
              <span style={{
                background: 'var(--warning, #f59e0b)',
                color: '#fff',
                borderRadius: '10px',
                padding: '1px 7px',
                fontSize: '0.7rem',
                fontWeight: 600
              }}>{pendingApprovalCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* Collapsible Filters Panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            className="filters-panel"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className="filters-grid">
              <div className="filter-item">
                <label><Calendar size={12} /> Month</label>
                <div className="month-input-wrapper">
                  <input
                    type="month"
                    className="month-input"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  />
                  {selectedMonth && (
                    <button className="month-clear-btn" onClick={() => setSelectedMonth('')} title="Clear">
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
              <div className="filter-item">
                <label><FileText size={12} /> Invoice Status</label>
                <SearchableDropdown
                  options={[
                    { id: 'pending', name: 'Pending' },
                    { id: 'paid', name: 'Paid' },
                    { id: 'overdue', name: 'Overdue' },
                    { id: 'partial', name: 'Partial' },
                    { id: 'cancelled', name: 'Cancelled' }
                  ]}
                  value={invoiceStatusFilter}
                  onChange={setInvoiceStatusFilter}
                  placeholder="Filter status..."
                  allLabel="All Status"
                  labelKey="name"
                  valueKey="id"
                />
              </div>
              <div className="filter-item">
                <label><Users size={12} /> Client</label>
                <SearchableDropdown
                  options={clients.map(c => ({ id: c.id.toString(), name: c.name }))}
                  value={invoiceClientFilter}
                  onChange={setInvoiceClientFilter}
                  placeholder="Filter client..."
                  allLabel="All Clients"
                  labelKey="name"
                  valueKey="id"
                />
              </div>
            </div>
            {activeFilterCount > 0 && (
              <div className="filters-actions">
                <button className="btn btn-ghost btn-sm" onClick={clearAllFilters}>
                  <X size={14} /> Clear Filters
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tabs */}
      <div className="client-tabs">
        <button className={`client-tab ${activeTab === 'list' ? 'active' : ''}`} onClick={() => setActiveTab('list')}>
          <Users size={16} /> Clients
        </button>
        <button className={`client-tab ${activeTab === 'invoices' ? 'active' : ''}`} onClick={() => setActiveTab('invoices')}>
          <FileText size={16} /> Invoices
        </button>
        <button className={`client-tab ${activeTab === 'sheet' ? 'active' : ''}`} onClick={() => setActiveTab('sheet')}>
          <Activity size={16} /> Sheet
        </button>
      </div>

      {/* ===== CLIENTS TAB ===== */}
      {activeTab === 'list' && (
        <>
          {/* Stats Blocks */}
          <div className="stat-blocks">
            <div className="stat-block green">
              <div className="stat-block-icon"><Users size={20} /></div>
              <div className="stat-block-content">
                <span className="stat-block-value">{stats.active}</span>
                <span className="stat-block-label">Active Clients</span>
              </div>
            </div>
            <div className="stat-block blue">
              <div className="stat-block-icon"><Bot size={20} /></div>
              <div className="stat-block-content">
                <span className="stat-block-value">{stats.activeBots} / {stats.totalBots}</span>
                <span className="stat-block-label">Active / Total Bots</span>
              </div>
            </div>
            <div className="stat-block">
              <div className="stat-block-icon"><Server size={20} /></div>
              <div className="stat-block-content">
                <span className="stat-block-value">{stats.totalDialers}</span>
                <span className="stat-block-label">Total Dialers</span>
              </div>
            </div>
            <div className="stat-block">
              <div className="stat-block-icon"><Server size={20} /></div>
              <div className="stat-block-content">
                <span className="stat-block-value">{stats.totalServers}</span>
                <span className="stat-block-label">Servers Used</span>
              </div>
            </div>
            <div className="stat-block orange">
              <div className="stat-block-icon"><AlertTriangle size={20} /></div>
              <div className="stat-block-content">
                <span className="stat-block-value">{stats.lowBalance}</span>
                <span className="stat-block-label">Low Balance</span>
              </div>
            </div>
            <div className="stat-block" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(59,130,246,0.04))' }}>
              <div className="stat-block-icon"><TrendingUp size={20} /></div>
              <div className="stat-block-content">
                <span className="stat-block-value">${stats.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                <span className="stat-block-label">Total Revenue</span>
              </div>
            </div>
            <div className="stat-block green">
              <div className="stat-block-icon"><Plus size={20} /></div>
              <div className="stat-block-content">
                <span className="stat-block-value">{stats.totalAdditions}</span>
                <span className="stat-block-label">Total Additions</span>
              </div>
            </div>
            <div className="stat-block" style={{ background: 'linear-gradient(135deg, rgba(239,68,68,0.08), rgba(239,68,68,0.04))' }}>
              <div className="stat-block-icon"><Minus size={20} /></div>
              <div className="stat-block-content">
                <span className="stat-block-value">{stats.totalRemovals}</span>
                <span className="stat-block-label">Total Removals</span>
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="table-toolbar">
            <div className="toolbar-left">
              <div className="search-box">
                <Search size={18} />
                <input type="text" placeholder="Search clients..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                {searchQuery && <button className="search-clear" onClick={() => setSearchQuery('')}><X size={14} /></button>}
              </div>
            </div>
            <div className="toolbar-right">
              <div className="filter-group">
                <SearchableDropdown
                  options={[
                    { id: 'active', name: 'Active' },
                    { id: 'suspended', name: 'Suspended' },
                    { id: 'pending', name: 'Pending' }
                  ]}
                  value={filterStatus}
                  onChange={setFilterStatus}
                  placeholder="Search status..."
                  allLabel="All Status"
                  labelKey="name"
                  valueKey="id"
                />
              </div>
              <div className="results-count">{filteredClients.length} of {clients.length} clients</div>
            </div>
          </div>

          {/* Bulk-selection bar with action buttons */}
          {selectedClientIds.size > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
              padding: '10px 14px', marginBottom: '10px',
              background: 'rgba(220, 38, 38, 0.08)',
              border: '1px solid rgba(220, 38, 38, 0.3)',
              borderRadius: 'var(--radius-md)'
            }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                <strong>{selectedClientIds.size}</strong> client{selectedClientIds.size === 1 ? '' : 's'} selected
              </span>
              <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                <button className="btn btn-success btn-sm" onClick={handleBulkReactivate} disabled={bulkBusy} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  {bulkBusy ? <Loader2 size={14} className="spin" /> : <CheckCircle size={14} />}
                  Reactivate
                </button>
                <button className="btn btn-danger btn-sm" onClick={handleBulkSuspend} disabled={bulkBusy} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  {bulkBusy ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />}
                  Suspend
                </button>
                <button className="btn btn-ghost btn-sm" onClick={clearClientSelection} disabled={bulkBusy}>Clear</button>
              </div>
            </div>
          )}

          {/* Clients Table */}
          <div className="table-container clients-table-compact">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '32px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={filteredClients.length > 0 && filteredClients.every(c => selectedClientIds.has(c.id))}
                      onChange={(e) => e.target.checked ? selectAllVisibleClients(filteredClients) : clearClientSelection()}
                      style={{ cursor: 'pointer' }}
                      title="Select all visible"
                    />
                  </th>
                  <th>Client Name</th>
                  <th style={{ width: '120px' }}>Contact</th>
                  <th style={{ width: '50px', textAlign: 'center' }}>Dialers</th>
                  <th style={{ width: '70px', textAlign: 'center' }}>Bots</th>
                  <th style={{ width: '120px', textAlign: 'right' }}>Balance</th>
                  <th style={{ width: '70px', textAlign: 'center' }}>Risk</th>
                  <th style={{ width: '85px', textAlign: 'center' }}>Status</th>
                  <th style={{ width: '36px' }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="empty-state">
                      <Users size={48} />
                      <p>No clients found</p>
                      <button className="btn btn-primary" onClick={() => openModal('add')}>
                        <UserPlus size={18} /> Add First Client
                      </button>
                    </td>
                  </tr>
                ) : (
                  filteredClients.map(client => {
                    return (
                      <tr key={client.id} className={`${client.grace_period_active ? 'grace-period-row' : ''} ${selectedClientIds.has(client.id) ? 'row-selected' : ''}`.trim()}>
                        <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedClientIds.has(client.id)}
                            onChange={() => toggleClientSelection(client.id)}
                            style={{ cursor: 'pointer', width: '15px', height: '15px' }}
                          />
                        </td>
                        <td>
                          <div className="client-info-compact clickable" onClick={() => navigate(`/clients/${client.id}`)}>
                            <span className="client-avatar-sm">{client.name?.charAt(0).toUpperCase()}</span>
                            <div className="client-name-col">
                              <span className="client-name">{client.name}</span>
                              {client.reseller_name && !client.reseller_is_default && (
                                <span className="client-contact-sub" title="Reseller">via {client.reseller_name}</span>
                              )}
                              {client.contact_person && (
                                <span className="client-contact-sub">{client.contact_person}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="contact-col">
                            <span className="client-phone">{client.phone || '-'}</span>
                            {client.whatsapp_number && (
                              <a href={getWhatsAppLink(client)} target="_blank" rel="noopener noreferrer" className="wa-link" title="WhatsApp">
                                <MessageCircle size={12} />
                              </a>
                            )}
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className="dialer-count-sm">{client.dialer_count || 0}</span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className="bot-counts-compact">
                            <span className="bot-active">{client.active_bots || 0}</span>
                            <span className="bot-sep">/</span>
                            <span className="bot-total">{client.total_bots || 0}</span>
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <span className={`balance-compact ${getBalanceClass(client.balance)}`}>
                            ${parseFloat(client.balance || 0).toFixed(2)}
                          </span>
                          {!!client.grace_period_active && <span className="grace-badge-sm">Grace</span>}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className={`risk-badge ${getRiskClass(client.risk_status)}`}>
                            {(client.risk_status || 'low').replace('_', ' ')}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className={`status-badge-sm ${getStatusClass(client.status)}`}>
                            {formatStatus(client.status)}
                          </span>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <button
                            className="action-btn-compact"
                            onClick={(e) => {
                              if (activeMenu?.id === client.id) { setActiveMenu(null); return }
                              const rect = e.currentTarget.getBoundingClientRect()
                              const spaceBelow = window.innerHeight - rect.bottom
                              const shouldOpenUp = spaceBelow < 250
                              setActiveMenu({
                                id: client.id, client,
                                top: shouldOpenUp ? rect.top - 250 : rect.bottom + 4,
                                left: rect.left - 160
                              })
                            }}
                          >
                            <MoreVertical size={16} />
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>

            {/* Fixed position dropdown menu */}
            <AnimatePresence>
              {activeMenu && (
                <motion.div
                  className="dropdown-menu-fixed"
                  style={{ position: 'fixed', top: activeMenu.top, left: activeMenu.left, zIndex: 9999 }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                >
                  <button onClick={() => { navigate(`/clients/${activeMenu.id}`); setActiveMenu(null) }}>
                    <Eye size={16} /> View Details
                  </button>
                  <button onClick={() => { openModal('edit', activeMenu.client); setActiveMenu(null) }}>
                    <Edit size={16} /> Edit Client
                  </button>
                  <button onClick={() => { openModal('credit', activeMenu.client); setActiveMenu(null) }}>
                    <DollarSign size={16} /> Add Credit
                  </button>
                  <button onClick={() => { openModal('assign', activeMenu.client); setActiveMenu(null) }}>
                    <Server size={16} /> Assign Dialer
                  </button>
                  <button onClick={() => { openNotes(activeMenu.client); setActiveMenu(null) }}>
                    <StickyNote size={16} /> Notes & Activity
                  </button>
                  <button onClick={() => { openSubscription(activeMenu.client) }}>
                    <Calendar size={16} /> Subscription
                  </button>
                  <button onClick={() => { openSampleVoices(activeMenu.client) }}>
                    <Music size={16} /> Sample Voices
                  </button>
                  {activeMenu.client.whatsapp_number && (
                    <a href={getWhatsAppLink(activeMenu.client)} target="_blank" rel="noopener noreferrer" className="dropdown-link-btn">
                      <MessageCircle size={16} /> WhatsApp
                    </a>
                  )}
                  <div className="dropdown-divider"></div>
                  <button className="danger" onClick={() => { handleDelete(activeMenu.client); setActiveMenu(null) }}>
                    <Trash2 size={16} /> Suspend Client
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {activeMenu && <div className="menu-backdrop" onClick={() => setActiveMenu(null)} />}
        </>
      )}

      {/* ===== INVOICES TAB ===== */}
      {activeTab === 'invoices' && (
        <>
          {/* Invoice Toolbar */}
          <div className="table-toolbar">
            <div className="toolbar-left">
              <div className="search-box">
                <Search size={18} />
                <input type="text" placeholder="Search invoices..." value={invoiceSearch} onChange={(e) => setInvoiceSearch(e.target.value)} />
                {invoiceSearch && <button className="search-clear" onClick={() => setInvoiceSearch('')}><X size={14} /></button>}
              </div>
            </div>
            <div className="toolbar-right">
              <div className="results-count">{filteredInvoices.length} of {invoices.length} invoices</div>
            </div>
          </div>

          {/* Invoices Table */}
          <div className="table-container invoices-table">
            {invoiceLoading ? (
              <div className="loading-container" style={{ padding: '60px' }}>
                <div className="loading-spinner"></div>
                <p>Loading invoices...</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Issue Date</th>
                    <th>Due Date</th>
                    <th>Period</th>
                    <th style={{ textAlign: 'center' }}>Bots</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th style={{ textAlign: 'center' }}>Approved</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="empty-state">
                        <FileText size={48} />
                        <p>No invoices found</p>
                        <button className="btn btn-primary" onClick={() => openInvoiceModal('generateInvoice')}>
                          <Receipt size={18} /> Generate First Invoice
                        </button>
                      </td>
                    </tr>
                  ) : (
                    filteredInvoices.map(invoice => {
                      const daysUntilDue = getDaysUntilDue(invoice.due_date)
                      return (
                        <tr key={invoice.id} className="invoice-row-clickable" onClick={() => setViewInvoice(invoice)}>
                          <td>
                            <span className="invoice-client-name">{invoice.client_name}</span>
                          </td>
                          <td>{formatDate(invoice.issue_date)}</td>
                          <td>
                            <div className="due-date-cell">
                              {formatDate(invoice.due_date)}
                              {invoice.status === 'pending' && daysUntilDue !== null && (
                                <span className={`due-countdown ${daysUntilDue < 0 ? 'overdue' : daysUntilDue <= 3 ? 'urgent' : 'normal'}`}>
                                  {daysUntilDue < 0 ? `${Math.abs(daysUntilDue)}d overdue` : daysUntilDue === 0 ? 'Due today' : `${daysUntilDue}d left`}
                                </span>
                              )}
                            </div>
                          </td>
                          <td><span className="period-cell">{formatPeriod(invoice.period_start, invoice.period_end)}</span></td>
                          <td style={{ textAlign: 'center' }}>{invoice.total_bots}</td>
                          <td style={{ textAlign: 'right' }}><span className="amount-cell">{formatCurrency(invoice.subtotal_usd)}</span></td>
                          <td style={{ textAlign: 'center' }}>
                            <span className={`invoice-status-badge ${getInvoiceStatusClass(invoice.status, invoice.due_date)}`}>
                              {formatInvoiceStatus(invoice.status, invoice.due_date)}
                            </span>
                            {invoice.is_bad_debt === 1 && <span className="bad-debt-tag">Bad Debt</span>}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {invoice.is_approved ? (
                              <span
                                className="approval-badge approved"
                                title={invoice.approved_by_name ? `Approved by ${invoice.approved_by_name}${invoice.approved_at ? ' on ' + new Date(invoice.approved_at).toLocaleString() : ''}` : 'Approved'}
                                style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.1 }}
                              >
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                  <CheckCircle size={14} /> Yes
                                </span>
                                {invoice.approved_by_name && (
                                  <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                                    by {invoice.approved_by_name}
                                  </span>
                                )}
                              </span>
                            ) : (
                              <span className="approval-badge pending"><AlertCircle size={14} /><span>No</span></span>
                            )}
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ===== INVOICE DETAIL POPUP ===== */}
      <AnimatePresence>
        {viewInvoice && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setViewInvoice(null)}>
            <motion.div className="invoice-popup" initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} onClick={(e) => e.stopPropagation()}>
              {/* Popup Header */}
              <div className="inv-popup-header">
                <div className="inv-popup-title">
                  <h2>{viewInvoice.client_name}</h2>
                  <span className="inv-popup-no">{viewInvoice.invoice_no}</span>
                </div>
                <div className="inv-popup-header-actions">
                  <span className={`invoice-status-badge ${getInvoiceStatusClass(viewInvoice.status, viewInvoice.due_date)}`}>
                    {formatInvoiceStatus(viewInvoice.status, viewInvoice.due_date)}
                  </span>
                  {viewInvoice.is_bad_debt === 1 && <span className="bad-debt-tag">Bad Debt</span>}
                  <button className="modal-close" onClick={() => setViewInvoice(null)}><X size={20} /></button>
                </div>
              </div>

              {/* Popup Info Grid */}
              <div className="inv-popup-body">
                <div className="inv-popup-info-grid">
                  <div className="inv-popup-info-item">
                    <span className="inv-info-label">Issue Date</span>
                    <span className="inv-info-value">{formatDate(viewInvoice.issue_date)}</span>
                  </div>
                  <div className="inv-popup-info-item">
                    <span className="inv-info-label">Due Date</span>
                    <span className="inv-info-value">
                      {formatDate(viewInvoice.due_date)}
                      {viewInvoice.status === 'pending' && (() => {
                        const d = getDaysUntilDue(viewInvoice.due_date)
                        return d !== null ? (
                          <span className={`due-countdown ${d < 0 ? 'overdue' : d <= 3 ? 'urgent' : 'normal'}`} style={{ marginLeft: 8 }}>
                            {d < 0 ? `${Math.abs(d)}d overdue` : d === 0 ? 'Due today' : `${d}d left`}
                          </span>
                        ) : null
                      })()}
                    </span>
                  </div>
                  <div className="inv-popup-info-item">
                    <span className="inv-info-label">Period</span>
                    <span className="inv-info-value">{formatPeriod(viewInvoice.period_start, viewInvoice.period_end)}</span>
                  </div>
                  <div className="inv-popup-info-item">
                    <span className="inv-info-label">Amount</span>
                    <span className="inv-info-value inv-info-amount">{formatCurrency(viewInvoice.subtotal_usd)}</span>
                  </div>
                  <div className="inv-popup-info-item">
                    <span className="inv-info-label">Bots</span>
                    <span className="inv-info-value">{viewInvoice.total_bots}</span>
                  </div>
                  <div className="inv-popup-info-item">
                    <span className="inv-info-label">Days</span>
                    <span className="inv-info-value">{viewInvoice.total_days}</span>
                  </div>
                  {viewInvoice.exchange_rate && (
                    <div className="inv-popup-info-item">
                      <span className="inv-info-label">PKR Amount</span>
                      <span className="inv-info-value">Rs. {((viewInvoice.subtotal_usd || 0) * (viewInvoice.exchange_rate || 280)).toLocaleString()}</span>
                    </div>
                  )}
                  <div className="inv-popup-info-item">
                    <span className="inv-info-label">Approved</span>
                    <span className="inv-info-value">
                      {viewInvoice.is_approved ? (
                        <span className="approval-badge approved"><CheckCircle size={14} /> Yes{viewInvoice.approved_by_name ? ` (${viewInvoice.approved_by_name})` : ''}</span>
                      ) : (
                        <span className="approval-badge pending"><AlertCircle size={14} /> No</span>
                      )}
                    </span>
                  </div>
                  {viewInvoice.paid_amount > 0 && (
                    <div className="inv-popup-info-item">
                      <span className="inv-info-label">Paid</span>
                      <span className="inv-info-value" style={{ color: '#22c55e', fontWeight: 600 }}>
                        {formatCurrency(viewInvoice.paid_amount)}
                        {viewInvoice.payment_currency === 'PKR' && viewInvoice.payment_amount_original && (
                          <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6 }}>
                            (Rs. {parseFloat(viewInvoice.payment_amount_original).toLocaleString()})
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  {viewInvoice.paid_reference && (
                    <div className="inv-popup-info-item">
                      <span className="inv-info-label">Reference</span>
                      <span className="inv-info-value">{viewInvoice.paid_reference}</span>
                    </div>
                  )}
                  {viewInvoice.notes && (
                    <div className="inv-popup-info-item" style={{ gridColumn: '1 / -1' }}>
                      <span className="inv-info-label">Notes</span>
                      <span className="inv-info-value">{viewInvoice.notes}</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="inv-popup-actions">
                  <button className="btn btn-secondary btn-sm" onClick={() => { downloadInvoicePdf(viewInvoice) }} disabled={downloading === viewInvoice.id}>
                    {downloading === viewInvoice.id ? <span className="btn-spinner-sm"></span> : <Download size={14} />} PDF
                  </button>
                  {viewInvoice.status !== 'paid' && (
                    <button className="btn btn-sm" style={{ background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', border: 'none' }} onClick={() => { openInvoiceModal('markPaid', viewInvoice); setViewInvoice(null) }}>
                      <CheckCircle size={14} /> Mark Paid
                    </button>
                  )}
                  <button className="btn btn-secondary btn-sm" onClick={() => { setEditingInvoice(viewInvoice); setShowInvoiceEditor(true); setViewInvoice(null) }}>
                    <Edit size={14} /> Edit
                  </button>
                  {viewInvoice.is_approved ? (
                    <button className="btn btn-sm" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', border: 'none' }} onClick={() => { handleUnapproveInvoice(viewInvoice); setViewInvoice(null) }}>
                      <Eye size={14} /> Unapprove
                    </button>
                  ) : (
                    <button className="btn btn-sm" style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6', border: 'none' }} onClick={() => { handleApproveInvoice(viewInvoice); setViewInvoice(null) }}>
                      <Eye size={14} /> Approve
                    </button>
                  )}
                  {viewInvoice.status !== 'paid' && !viewInvoice.is_bad_debt && (
                    <button className="btn btn-sm" style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6', border: 'none' }} onClick={() => { setBadDebtInvoice(viewInvoice); setShowBadDebtModal(true); setViewInvoice(null) }}>
                      <ShieldAlert size={14} /> Bad Debt
                    </button>
                  )}
                  {(() => {
                    const client = clients.find(c => c.id === viewInvoice.client_id)
                    const waLink = client ? getWhatsAppLink(client, viewInvoice) : null
                    return waLink ? (
                      <a href={waLink} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ background: 'rgba(37, 211, 102, 0.15)', color: '#25d366', border: 'none', textDecoration: 'none' }}>
                        <MessageCircle size={14} /> WhatsApp
                      </a>
                    ) : null
                  })()}
                  <button className="btn btn-sm" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: 'none', marginLeft: 'auto' }} onClick={() => { handleDeleteInvoice(viewInvoice); setViewInvoice(null) }}>
                    <Trash2 size={14} /> Delete
                  </button>
                </div>

                {/* Line Items */}
                <div className="inv-popup-line-items">
                  <div className="line-items-header">
                    <h4>Line Items</h4>
                    <button className="btn btn-secondary btn-sm" style={{ marginRight: 6 }} onClick={() => setEditingLineItems(!editingLineItems)}>
                      {editingLineItems ? <><CheckCircle size={14} /> Done</> : <><Edit size={14} /> Edit</>}
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setCurrentInvoice(viewInvoice); openInvoiceModal('addLineItem', viewInvoice); setViewInvoice(null) }}>
                      <Plus size={14} /> Add Item
                    </button>
                  </div>
                  <table className="line-items-table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Campaign</th>
                        <th style={{ textAlign: 'center' }}>QTY</th>
                        <th style={{ textAlign: 'right' }}>Rate</th>
                        <th style={{ textAlign: 'center' }}>Days</th>
                        <th style={{ textAlign: 'right' }}>Amount</th>
                        <th style={{ width: '40px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewInvoice.line_items || []).map((item, idx) => (
                        <tr key={idx} className={item.is_previous_balance ? 'previous-balance-row' : ''}>
                          <td>
                            <div className="line-item-desc">
                              <span>{item.description || `Bots Deployed on ${item.dialer_name || item.campaign || 'Unknown'}`}</span>
                              {item.date_from && item.date_to && (
                                <span className="line-item-dates">{formatDate(item.date_from)} - {formatDate(item.date_to)}</span>
                              )}
                              {item.is_previous_balance && <span className="previous-balance-badge">Previous Balance</span>}
                            </div>
                          </td>
                          <td><span className={`campaign-badge ${item.is_previous_balance ? 'prev-balance' : ''}`}>{item.campaign || item.dialer_name || '-'}</span></td>
                          <td style={{ textAlign: 'center' }}>{item.qty || item.bot_count || 0}</td>
                          <td style={{ textAlign: 'right' }}>${parseFloat(item.rate || item.price_per_bot || 0).toFixed(2)}</td>
                          <td style={{ textAlign: 'center' }}>{item.days || 30}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>${parseFloat(item.amount_usd || 0).toFixed(2)}</td>
                          <td>
                            {editingLineItems && (
                              <button className="line-item-delete" onClick={() => handleDeleteLineItem(viewInvoice.id, idx)} title="Delete">
                                <X size={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {(!viewInvoice.line_items || viewInvoice.line_items.length === 0) && (
                        <tr><td colSpan="7" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '20px' }}>No line items</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Payment History */}
                <div className="inv-popup-line-items" style={{ marginTop: 16 }}>
                  <div className="line-items-header">
                    <h4>Payment History</h4>
                    <button className="btn btn-primary btn-sm" onClick={() => { openInvoiceModal('markPaid', viewInvoice); setViewInvoice(null) }}>
                      <Plus size={14} /> Record Payment
                    </button>
                  </div>
                  {invoicePayments.length > 0 ? (
                    <table className="inv-popup-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Amount (USD)</th>
                          <th>Currency</th>
                          <th>Original Amount</th>
                          <th>Reference</th>
                          <th>Recorded By</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoicePayments.map(p => (
                          <React.Fragment key={p.id}>
                            <tr>
                              <td>{new Date(p.payment_date).toLocaleDateString()}</td>
                              <td style={{ fontWeight: 600, color: '#22c55e' }}>{formatCurrency(p.amount_usd)}</td>
                              <td>{p.payment_currency || 'USD'}</td>
                              <td>{p.amount_original ? `Rs. ${parseFloat(p.amount_original).toLocaleString()}` : '-'}</td>
                              <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.payment_reference || ''}>{p.payment_reference || '-'}</td>
                              <td>{p.created_by_name || '-'}</td>
                              <td style={{ display: 'flex', gap: 4, whiteSpace: 'nowrap' }}>
                                <button title="Edit" onClick={() => setEditingPayment(editingPayment?.id === p.id ? null : { id: p.id, amount_usd: p.amount_usd, payment_currency: p.payment_currency, amount_original: p.amount_original, exchange_rate: p.exchange_rate, payment_reference: p.payment_reference, payment_date: p.payment_date })} style={{ color: editingPayment?.id === p.id ? '#f59e0b' : '#3b82f6', background: 'none', border: 'none', cursor: 'pointer' }}><Edit size={14} /></button>
                                <button title="Delete" onClick={() => handleDeletePayment(p.id)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}><Trash2 size={14} /></button>
                              </td>
                            </tr>
                            {editingPayment?.id === p.id && (
                              <tr>
                                <td colSpan="7" style={{ padding: '12px 14px', background: 'rgba(59, 130, 246, 0.04)' }}>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: 10 }}>
                                    <div>
                                      <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>DATE</label>
                                      <input type="date" value={editingPayment.payment_date?.split('T')[0] || ''} onChange={e => setEditingPayment(prev => ({ ...prev, payment_date: e.target.value }))} style={{ width: '100%', padding: '6px 8px', fontSize: '0.82rem', border: '1px solid var(--border-color)', borderRadius: 6 }} />
                                    </div>
                                    <div>
                                      <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>AMOUNT (USD)</label>
                                      <input type="number" step="0.01" value={editingPayment.amount_usd} onChange={e => setEditingPayment(prev => ({ ...prev, amount_usd: e.target.value }))} style={{ width: '100%', padding: '6px 8px', fontSize: '0.82rem', border: '1px solid var(--border-color)', borderRadius: 6 }} />
                                    </div>
                                    <div>
                                      <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>CURRENCY</label>
                                      <select value={editingPayment.payment_currency || 'USD'} onChange={e => setEditingPayment(prev => ({ ...prev, payment_currency: e.target.value }))} style={{ width: '100%', padding: '6px 8px', fontSize: '0.82rem', border: '1px solid var(--border-color)', borderRadius: 6 }}>
                                        <option value="USD">USD</option><option value="PKR">PKR</option>
                                      </select>
                                    </div>
                                    <div>
                                      <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>ORIGINAL AMOUNT</label>
                                      <input type="number" step="0.01" value={editingPayment.amount_original || ''} onChange={e => setEditingPayment(prev => ({ ...prev, amount_original: e.target.value }))} placeholder="PKR amount" style={{ width: '100%', padding: '6px 8px', fontSize: '0.82rem', border: '1px solid var(--border-color)', borderRadius: 6 }} />
                                    </div>
                                    <div>
                                      <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>REFERENCE</label>
                                      <input type="text" value={editingPayment.payment_reference || ''} onChange={e => setEditingPayment(prev => ({ ...prev, payment_reference: e.target.value }))} style={{ width: '100%', padding: '6px 8px', fontSize: '0.82rem', border: '1px solid var(--border-color)', borderRadius: 6 }} />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                                      <button onClick={() => handleSavePayment(p.id)} style={{ padding: '6px 16px', fontSize: '0.82rem', background: '#22c55e', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircle size={14} /> Save</button>
                                      <button onClick={() => setEditingPayment(null)} style={{ padding: '6px 12px', fontSize: '0.82rem', background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '16px 0' }}>No payments recorded</p>
                  )}
                  {invoicePayments.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid var(--border-color)', fontWeight: 600 }}>
                      <span>Total Paid</span>
                      <span style={{ color: '#22c55e' }}>{formatCurrency(invoicePayments.reduce((sum, p) => sum + parseFloat(p.amount_usd || 0), 0))}</span>
                    </div>
                  )}
                </div>

                {/* Follow-up Date */}
                <div className="inv-popup-line-items" style={{ marginTop: 16 }}>
                  <div className="line-items-header">
                    <h4>Follow-up</h4>
                  </div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', padding: '8px 0' }}>
                    <div style={{ flex: '0 0 160px' }}>
                      <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Follow-up Date</label>
                      <input
                        type="date"
                        value={viewInvoice.follow_up_date?.split('T')[0] || ''}
                        onChange={async (e) => {
                          const val = e.target.value
                          try {
                            await clientAdminApi.updateInvoice(viewInvoice.id, { follow_up_date: val || null })
                            setViewInvoice(prev => ({ ...prev, follow_up_date: val }))
                            showToast('Follow-up date updated')
                          } catch { showToast('Failed to update', 'error') }
                        }}
                        style={{ width: '100%', padding: '6px 8px', fontSize: '0.85rem', border: '1px solid var(--border-color)', borderRadius: 6, background: 'var(--bg-card)', color: 'var(--text-primary)' }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Note</label>
                      <input
                        type="text"
                        placeholder="Follow-up note..."
                        defaultValue={viewInvoice.follow_up_note || ''}
                        onBlur={async (e) => {
                          const val = e.target.value
                          if (val === (viewInvoice.follow_up_note || '')) return
                          try {
                            await clientAdminApi.updateInvoice(viewInvoice.id, { follow_up_note: val })
                            setViewInvoice(prev => ({ ...prev, follow_up_note: val }))
                            showToast('Follow-up note updated')
                          } catch { showToast('Failed to update', 'error') }
                        }}
                        style={{ width: '100%', padding: '6px 8px', fontSize: '0.85rem', border: '1px solid var(--border-color)', borderRadius: 6, background: 'var(--bg-card)', color: 'var(--text-primary)' }}
                      />
                    </div>
                  </div>
                </div>

                {viewInvoice.status_changed_by_name && (
                  <div className="inv-popup-footer-info">
                    Status changed by <strong>{viewInvoice.status_changed_by_name}</strong> on {new Date(viewInvoice.status_changed_at).toLocaleString()}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== SHEET TAB ===== */}
      {activeTab === 'sheet' && (
        <>
          {/* Controls */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <input type="month" value={sheetMonth} onChange={(e) => setSheetMonth(e.target.value)}
              style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid var(--border-color)', fontSize: '0.9rem', background: 'var(--bg-card)', color: 'var(--text-primary)', fontWeight: 500 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => {
                const rows = botChangesLog.map(c => [
                  c.client_name,
                  new Date(c.effective_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
                  c.change_type === 'initial' ? 'OnBoard' : c.change_type === 'addition' ? 'Added' : c.change_type === 'removal' ? 'Remove' : c.change_type,
                  c.change_type === 'removal' ? -Math.abs(c.bot_count) : c.bot_count,
                  '', c.campaign || c.dialer_name || '', c.notes || ''
                ].join('\t'))
                navigator.clipboard.writeText('Client\tDate\tStatus\tTotal\tRemaining\tCampaign\tReason\n' + rows.join('\n'))
                showToast('Copied to clipboard')
              }}>
                <Download size={14} /> Copy
              </button>
              <button className="btn btn-primary btn-sm" onClick={() => setShowAddRow(true)}>
                <Plus size={14} /> Add Row
              </button>
            </div>
          </div>

          {/* Period Header */}
          <div style={{ background: '#1a1a2e', color: '#fff', textAlign: 'center', padding: '14px 0', fontSize: '1.3rem', fontWeight: 700, borderRadius: '8px 8px 0 0' }}>
            {(() => {
              if (!sheetMonth) return 'All Time'
              const [y, m] = sheetMonth.split('-').map(Number)
              const monthName = new Date(y, m - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
              return monthName
            })()}
          </div>

          {/* Table */}
          {botChangesLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading...</div>
          ) : (
            <div className="table-container" style={{ borderRadius: '0 0 8px 8px', marginTop: 0 }}>
              <table className="data-table" style={{ margin: 0 }}>
                <thead>
                  <tr style={{ background: '#b91c1c' }}>
                    <th style={{ color: '#fff', padding: '10px 14px' }}>Client</th>
                    <th style={{ color: '#fff', padding: '10px 14px' }}>Date</th>
                    <th style={{ color: '#fff', padding: '10px 14px' }}>Status</th>
                    <th style={{ color: '#fff', padding: '10px 14px', textAlign: 'center' }}>Total</th>
                    <th style={{ color: '#fff', padding: '10px 14px', textAlign: 'center' }}>Remaining</th>
                    <th style={{ color: '#fff', padding: '10px 14px' }}>Campaign</th>
                    <th style={{ color: '#fff', padding: '10px 14px' }}>Reason</th>
                    <th style={{ color: '#fff', padding: '10px 14px', width: 70 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {/* Add Row */}
                  {showAddRow && (
                    <tr style={{ background: 'rgba(59,130,246,0.06)', borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '6px 8px' }}>
                        <select value={newRow.client_id} onChange={e => setNewRow(p => ({...p, client_id: e.target.value}))} style={{ width: '100%', padding: '5px', fontSize: '0.82rem', border: '1px solid var(--border-color)', borderRadius: 4 }}>
                          <option value="">Select client...</option>
                          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '6px 8px' }}><input type="date" value={newRow.effective_date} onChange={e => setNewRow(p => ({...p, effective_date: e.target.value}))} style={{ padding: '5px', fontSize: '0.82rem', border: '1px solid var(--border-color)', borderRadius: 4, width: '100%' }} /></td>
                      <td style={{ padding: '6px 8px' }}>
                        <select value={newRow.change_type} onChange={e => setNewRow(p => ({...p, change_type: e.target.value}))} style={{ padding: '5px', fontSize: '0.82rem', border: '1px solid var(--border-color)', borderRadius: 4 }}>
                          <option value="addition">Added</option>
                          <option value="removal">Remove</option>
                          <option value="initial">OnBoard</option>
                        </select>
                      </td>
                      <td style={{ padding: '6px 8px' }}><input type="number" value={newRow.bot_count} onChange={e => setNewRow(p => ({...p, bot_count: e.target.value}))} placeholder="0" style={{ width: 60, padding: '5px', fontSize: '0.82rem', border: '1px solid var(--border-color)', borderRadius: 4, textAlign: 'center' }} /></td>
                      <td style={{ padding: '6px 8px' }}><input type="number" value={newRow.remaining || ''} onChange={e => setNewRow(p => ({...p, remaining: e.target.value}))} placeholder="-" style={{ width: 60, padding: '5px', fontSize: '0.82rem', border: '1px solid var(--border-color)', borderRadius: 4, textAlign: 'center' }} /></td>
                      <td style={{ padding: '6px 8px' }}></td>
                      <td style={{ padding: '6px 8px' }}><input type="text" value={newRow.notes} onChange={e => setNewRow(p => ({...p, notes: e.target.value}))} placeholder="Reason" style={{ width: '100%', padding: '5px', fontSize: '0.82rem', border: '1px solid var(--border-color)', borderRadius: 4 }} /></td>
                      <td style={{ padding: '6px 8px', display: 'flex', gap: 4 }}>
                        <button onClick={async () => {
                          if (!newRow.client_id || !newRow.bot_count) { showToast('Select client and enter bot count', 'error'); return }
                          try {
                            await clientAdminApi.addBotChangeLog({ ...newRow, bot_count: parseInt(newRow.bot_count), remaining: newRow.remaining ? parseInt(newRow.remaining) : null, created_by_name: currentUser?.username })
                            showToast('Added'); setShowAddRow(false); setNewRow({ client_id: '', change_type: 'addition', bot_count: '', effective_date: new Date().toISOString().split('T')[0], notes: '' }); loadBotChangesLog()
                          } catch { showToast('Failed', 'error') }
                        }} style={{ padding: '4px 10px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.8rem' }}>Save</button>
                        <button onClick={() => setShowAddRow(false)} style={{ padding: '4px 8px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 4, cursor: 'pointer', fontSize: '0.8rem' }}>X</button>
                      </td>
                    </tr>
                  )}
                  {botChangesLog.map((c, i) => {
                    const statusMap = { initial: 'OnBoard', addition: 'Added', removal: 'Remove' }
                    const statusLabel = statusMap[c.change_type] || c.change_type
                    const statusColor = c.change_type === 'removal' ? '#ef4444' : c.change_type === 'initial' ? '#f59e0b' : '#22c55e'
                    const total = c.change_type === 'removal' ? -Math.abs(c.bot_count) : c.bot_count
                    const isEditing = editingChange?.id === c.id
                    if (isEditing) {
                      return (
                        <tr key={c.id} style={{ background: 'rgba(59,130,246,0.06)', borderBottom: '1px solid var(--border-color)' }}>
                          <td style={{ padding: '6px 8px' }}>{c.client_name}</td>
                          <td style={{ padding: '6px 8px' }}><input type="date" value={editingChange.effective_date?.split('T')[0] || ''} onChange={e => setEditingChange(p => ({...p, effective_date: e.target.value}))} style={{ padding: '5px', fontSize: '0.82rem', border: '1px solid var(--border-color)', borderRadius: 4 }} /></td>
                          <td style={{ padding: '6px 8px' }}>
                            <select value={editingChange.change_type} onChange={e => setEditingChange(p => ({...p, change_type: e.target.value}))} style={{ padding: '5px', fontSize: '0.82rem', border: '1px solid var(--border-color)', borderRadius: 4 }}>
                              <option value="addition">Added</option><option value="removal">Remove</option><option value="initial">OnBoard</option>
                            </select>
                          </td>
                          <td style={{ padding: '6px 8px' }}><input type="number" value={editingChange.bot_count} onChange={e => setEditingChange(p => ({...p, bot_count: e.target.value}))} style={{ width: 60, padding: '5px', fontSize: '0.82rem', border: '1px solid var(--border-color)', borderRadius: 4, textAlign: 'center' }} /></td>
                          <td style={{ padding: '6px 8px' }}><input type="number" value={editingChange.remaining || ''} onChange={e => setEditingChange(p => ({...p, remaining: e.target.value}))} placeholder="-" style={{ width: 60, padding: '5px', fontSize: '0.82rem', border: '1px solid var(--border-color)', borderRadius: 4, textAlign: 'center' }} /></td>
                          <td style={{ padding: '6px 8px' }}>{c.dialer_name || ''}</td>
                          <td style={{ padding: '6px 8px' }}><input type="text" value={editingChange.notes || ''} onChange={e => setEditingChange(p => ({...p, notes: e.target.value}))} style={{ width: '100%', padding: '5px', fontSize: '0.82rem', border: '1px solid var(--border-color)', borderRadius: 4 }} /></td>
                          <td style={{ padding: '6px 8px', display: 'flex', gap: 4 }}>
                            <button onClick={async () => {
                              try {
                                await clientAdminApi.updateBotChangeLog(c.id, { change_type: editingChange.change_type, bot_count: parseInt(editingChange.bot_count), remaining: editingChange.remaining ? parseInt(editingChange.remaining) : null, effective_date: editingChange.effective_date?.split('T')[0], notes: editingChange.notes })
                                showToast('Updated'); setEditingChange(null); loadBotChangesLog()
                              } catch { showToast('Failed', 'error') }
                            }} style={{ padding: '4px 10px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.8rem' }}>Save</button>
                            <button onClick={() => setEditingChange(null)} style={{ padding: '4px 8px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 4, cursor: 'pointer', fontSize: '0.8rem' }}>X</button>
                          </td>
                        </tr>
                      )
                    }
                    return (
                      <tr key={c.id || i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '10px 14px' }}>{c.client_name}</td>
                        <td style={{ padding: '10px 14px' }}>{new Date(c.effective_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</td>
                        <td style={{ padding: '10px 14px', color: statusColor, fontStyle: 'italic' }}>{statusLabel}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 600 }}>{total}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'center' }}>{c.remaining ?? '-'}</td>
                        <td style={{ padding: '10px 14px' }}>{c.dialer_name || ''}</td>
                        <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{c.notes || ''}</td>
                        <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>
                          <button onClick={() => setEditingChange({ id: c.id, change_type: c.change_type, bot_count: c.bot_count, remaining: c.remaining, effective_date: c.effective_date, notes: c.notes })} style={{ color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', padding: 2 }} title="Edit"><Edit size={13} /></button>
                          <button onClick={async () => { if (!window.confirm('Delete this entry?')) return; try { await clientAdminApi.deleteBotChangeLog(c.id); showToast('Deleted'); loadBotChangesLog() } catch { showToast('Failed', 'error') } }} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: 2 }} title="Delete"><Trash2 size={13} /></button>
                        </td>
                      </tr>
                    )
                  })}
                  {botChangesLog.length === 0 && !showAddRow && (
                    <tr><td colSpan="8" style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>No records for this month</td></tr>
                  )}
                </tbody>
                {botChangesLog.length > 0 && (
                  <tfoot>
                    <tr style={{ background: '#b91c1c' }}>
                      <td colSpan="3" style={{ color: '#fff', padding: '10px 14px', fontWeight: 700, textAlign: 'right' }}>Total</td>
                      <td style={{ color: '#fff', padding: '10px 14px', textAlign: 'center', fontWeight: 700 }}>
                        {botChangesLog.reduce((sum, c) => sum + (c.change_type === 'removal' ? -Math.abs(c.bot_count) : c.bot_count), 0)}
                      </td>
                      <td style={{ color: '#fff', padding: '10px 14px', textAlign: 'center', fontWeight: 700 }}>
                        {botChangesLog.reduce((sum, c) => sum + Math.abs(c.bot_count), 0)}
                      </td>
                      <td colSpan="3" style={{ padding: '10px 14px' }}></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </>
      )}

      {/* ===== MAIN MODAL ===== */}
      <AnimatePresence>
        {showModal && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowModal(false)}>
            <motion.div className="modal" initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  {modalMode === 'add' && 'Add New Client'}
                  {modalMode === 'edit' && 'Edit Client'}
                  {modalMode === 'credit' && `Add Credit - ${currentClient?.name}`}
                  {modalMode === 'assign' && `Assign Dialer - ${currentClient?.name}`}
                  {modalMode === 'markPaid' && `Mark as Paid - ${currentInvoice?.invoice_no}`}
                  {modalMode === 'markPartial' && `Mark as Partial - ${currentInvoice?.invoice_no}`}
                  {modalMode === 'generateInvoice' && 'Generate Invoice'}
                  {modalMode === 'editInvoice' && `Edit Invoice - ${currentInvoice?.invoice_no}`}
                  {modalMode === 'addLineItem' && `Add Line Item - ${currentInvoice?.invoice_no}`}
                </h2>
                <button className="modal-close" onClick={() => setShowModal(false)}><X size={20} /></button>
              </div>

              {['add', 'edit', 'credit', 'assign'].includes(modalMode) && (
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {(modalMode === 'add' || modalMode === 'edit') && (
                    <>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Name *</label>
                          <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="Client / Company name" />
                        </div>
                        <div className="form-group">
                          <label>Address</label>
                          <input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Business address" />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Contact Person</label>
                          <input type="text" value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} placeholder="Contact person name" />
                        </div>
                        <div className="form-group">
                          <label>Contact Number</label>
                          <input type="text" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+1 234 567 8900" />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>WhatsApp Number</label>
                          <input type="text" value={formData.whatsapp_number} onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })} placeholder="+92 300 1234567" />
                        </div>
                        <div className="form-group">
                          <label>Follow-up Date</label>
                          <input type="date" value={formData.follow_up_date} onChange={(e) => setFormData({ ...formData, follow_up_date: e.target.value })} />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Server IP</label>
                          <input type="text" value={formData.server_ip} onChange={(e) => setFormData({ ...formData, server_ip: e.target.value })} placeholder="192.168.1.1" />
                        </div>
                        <div className="form-group">
                          <label>Status</label>
                          <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                            <option value="active">Active</option>
                            <option value="suspended">Suspended</option>
                            <option value="under_process">Under Process</option>
                            <option value="trial_period">Trial Period</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Risk Status</label>
                          <select value={formData.risk_status} onChange={(e) => setFormData({ ...formData, risk_status: e.target.value })}>
                            <option value="low">Low Risk</option>
                            <option value="medium">Medium Risk</option>
                            <option value="high">High Risk</option>
                            <option value="bad_debt">Bad Debt</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Target Bots</label>
                          <input type="number" value={formData.target_bots} onChange={(e) => setFormData({ ...formData, target_bots: parseInt(e.target.value) || 0 })} placeholder="0" />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Username *</label>
                          <input type="text" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} required={modalMode === 'add'} disabled={modalMode === 'edit'} placeholder="Login username" />
                        </div>
                        <div className="form-group">
                          <label>{modalMode === 'add' ? 'Password *' : 'New Password'}</label>
                          <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required={modalMode === 'add'} placeholder={modalMode === 'edit' ? 'Leave blank to keep current' : 'Password'} />
                        </div>
                      </div>

                      {modalMode === 'add' && (
                        <div className="form-row">
                          <div className="form-group">
                            <label>Initial Balance</label>
                            <input type="number" step="0.01" value={formData.balance} onChange={(e) => setFormData({ ...formData, balance: e.target.value })} placeholder="0.00" />
                          </div>
                          <div className="form-group">
                            <label>Default Bot Price</label>
                            <input type="number" step="0.01" value={formData.default_bot_price} onChange={(e) => setFormData({ ...formData, default_bot_price: e.target.value })} placeholder="15.00" />
                          </div>
                        </div>
                      )}

                      {modalMode === 'edit' && (
                        <>
                          <div className="form-row">
                            <div className="form-group">
                              <label>Default Bot Price</label>
                              <input type="number" step="0.01" value={formData.default_bot_price} onChange={(e) => setFormData({ ...formData, default_bot_price: e.target.value })} placeholder="15.00" />
                            </div>
                            <div className="form-group">
                              <label>Initial Invoice Day (first invoice only)</label>
                              <select value={formData.invoice_day} onChange={(e) => setFormData({ ...formData, invoice_day: parseInt(e.target.value) })}>
                                {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                                  <option key={day} value={day}>{day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'} of each month</option>
                                ))}
                              </select>
                              <small style={{ display: 'block', marginTop: '6px', color: 'var(--text-muted)', fontSize: '0.75rem', lineHeight: '1.4' }}>
                                Only used for this client's <strong>very first</strong> invoice. After that, the next invoice is auto-generated the day after the previous period ends — the cycle length (15 days, 30 days, or whatever the last invoice was) carries forward automatically. You don't need to change this date each month.
                              </small>
                            </div>
                          </div>
                          <NextCyclePreview clientId={currentClient?.id} />
                        </>
                      )}

                      <div className="form-row">
                        <div className="form-group">
                          <label>Payment Terms (days)</label>
                          <input type="number" value={formData.payment_terms_days} onChange={(e) => setFormData({ ...formData, payment_terms_days: parseInt(e.target.value) || 3 })} placeholder="3" />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Notes</label>
                        <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} placeholder="Internal notes about this client..." />
                      </div>

                      <div className="form-group">
                        <label>Reseller</label>
                        <select value={formData.reseller_id} onChange={(e) => setFormData({ ...formData, reseller_id: e.target.value })}>
                          <option value="">{resellers.find(r => r.is_default)?.name || 'Redstone'} (default)</option>
                          {resellers.filter(r => !r.is_default).map(r => (
                            <option key={r.id} value={String(r.id)}>{r.name}{r.company && r.company !== r.name ? ` (${r.company})` : ''}{r.status !== 'active' ? ` — ${r.status}` : ''}</option>
                          ))}
                        </select>
                        <small style={{ display: 'block', marginTop: '6px', color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                          Leave on the default for Redstone's own clients (they keep their own branding below). Another reseller's client sees that reseller's branding, theme and layout, and shows up in that reseller's portal.
                        </small>
                      </div>

                      <ClientBrandingFields
                        formData={formData}
                        setFormData={setFormData}
                        client={modalMode === 'edit' ? currentClient : null}
                        pending={pendingBranding}
                        setPending={setPendingBranding}
                        onError={setBrandingError}
                        inheritedFrom={resellers.find(r => !r.is_default && String(r.id) === String(formData.reseller_id)) || null}
                      />
                      {brandingError && <div className="tw-mt-2 tw-rounded-md tw-bg-red-500/10 tw-px-3 tw-py-2 tw-text-sm tw-text-red-300">{brandingError}</div>}
                    </>
                  )}

                  {modalMode === 'credit' && (
                    <>
                      <div className="current-balance">
                        <span>Current Balance:</span>
                        <strong>{formatCurrency(currentClient?.balance)}</strong>
                      </div>
                      <div className="form-group">
                        <label>Amount to Add *</label>
                        <input type="number" step="0.01" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} required placeholder="100.00" autoFocus />
                      </div>
                      <div className="form-group">
                        <label>Description</label>
                        <input type="text" value={creditDescription} onChange={(e) => setCreditDescription(e.target.value)} placeholder="Payment received, etc." />
                      </div>
                    </>
                  )}

                  {modalMode === 'assign' && (
                    <>
                      <div className="form-group">
                        <label>Select Dialer *</label>
                        <SearchableDropdown
                          options={availableDialers.map(d => ({ id: d.id, name: `${d.name} (${d.bot_count} bots)` }))}
                          value={selectedDialer} onChange={setSelectedDialer}
                          placeholder="Search dialers..." allLabel="Choose a dialer..."
                          labelKey="name" valueKey="id"
                        />
                        {availableDialers.length === 0 && <p className="form-hint warning">No unassigned dialers available</p>}
                      </div>
                      <div className="form-group">
                        <label>Custom Bot Price (optional)</label>
                        <input type="number" step="0.01" value={customBotPrice} onChange={(e) => setCustomBotPrice(e.target.value)} placeholder={`Default: $${currentClient?.default_bot_price || 15}/bot`} />
                        <p className="form-hint">Leave blank to use client's default rate</p>
                      </div>
                    </>
                  )}
                </div>

                {['add', 'edit', 'credit', 'assign'].includes(modalMode) && (
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">
                      {modalMode === 'add' && 'Create Client'}
                      {modalMode === 'edit' && 'Save Changes'}
                      {modalMode === 'credit' && 'Add Credit'}
                      {modalMode === 'assign' && 'Assign Dialer'}
                    </button>
                  </div>
                )}
              </form>
              )}

              {/* Mark Paid / Partial Form - with currency support */}
              {(modalMode === 'markPaid' || modalMode === 'markPartial') && (
                <form onSubmit={handleMarkPaid}>
                  <div className="modal-body">
                    <div className="current-balance" style={{ marginBottom: '20px' }}>
                      <span>Invoice Amount</span>
                      <strong>{formatCurrency(currentInvoice?.subtotal_usd || 0)}</strong>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Payment Currency</label>
                        <select value={paymentCurrency} onChange={(e) => {
                          setPaymentCurrency(e.target.value)
                          if (e.target.value === 'PKR' && paidAmount) {
                            const rate = parseFloat(paymentExchangeRate) || 280
                            setPaymentAmountOriginal((parseFloat(paidAmount) * rate).toFixed(2))
                          }
                        }}>
                          <option value="USD">USD (US Dollar)</option>
                          <option value="PKR">PKR (Pakistani Rupee)</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Amount Paid (USD) *</label>
                        <input type="number" step="0.01" value={paidAmount} onChange={(e) => {
                          setPaidAmount(e.target.value)
                          if (paymentCurrency === 'PKR' && e.target.value) {
                            const rate = parseFloat(paymentExchangeRate) || 280
                            setPaymentAmountOriginal((parseFloat(e.target.value) * rate).toFixed(2))
                          }
                        }} required />
                      </div>
                    </div>

                    {paymentCurrency === 'PKR' && (
                      <div className="form-row">
                        <div className="form-group">
                          <label>Amount Received (PKR)</label>
                          <input type="number" step="0.01" value={paymentAmountOriginal} onChange={(e) => {
                            setPaymentAmountOriginal(e.target.value)
                            const rate = parseFloat(paymentExchangeRate) || 280
                            if (e.target.value) setPaidAmount((parseFloat(e.target.value) / rate).toFixed(2))
                          }} placeholder="PKR amount received" />
                        </div>
                        <div className="form-group">
                          <label>Exchange Rate (PKR/USD)</label>
                          <input type="number" step="0.01" value={paymentExchangeRate} onChange={(e) => {
                            setPaymentExchangeRate(e.target.value)
                            if (paymentAmountOriginal && e.target.value) {
                              setPaidAmount((parseFloat(paymentAmountOriginal) / parseFloat(e.target.value)).toFixed(2))
                            }
                          }} placeholder="280" />
                        </div>
                      </div>
                    )}

                    <div className="form-row">
                      <div className="form-group">
                        <label>Payment Date</label>
                        <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label>Payment Reference</label>
                        <input type="text" value={paidReference} onChange={(e) => setPaidReference(e.target.value)} placeholder="Transaction ID, check number, etc." />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">
                      <CheckCircle size={16} /> Mark as {modalMode === 'markPaid' ? 'Paid' : 'Partial'}
                    </button>
                  </div>
                </form>
              )}

              {/* Generate Invoice Form */}
              {modalMode === 'generateInvoice' && (
                <form onSubmit={handleGenerateInvoice}>
                  <div className="modal-body">
                    <div className="form-group">
                      <label>Client *</label>
                      <SearchableDropdown
                        options={clients.filter(c => c.status === 'active').map(c => ({ id: c.id.toString(), name: c.name }))}
                        value={generateClientId} onChange={setGenerateClientId}
                        placeholder="Select client..." labelKey="name" valueKey="id" showAllOption={false}
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Issue Date</label>
                        <input type="date" value={generateIssueDate} onChange={(e) => setGenerateIssueDate(e.target.value)} />
                        <p className="form-hint">Leave empty to use today's date</p>
                      </div>
                      <div className="form-group">
                        <label>Exchange Rate (PKR/USD)</label>
                        <input type="number" step="0.01" value={generateExchangeRate} onChange={(e) => setGenerateExchangeRate(e.target.value)} placeholder="280" />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Period Start</label>
                        <input type="date" value={generatePeriodStart} onChange={(e) => setGeneratePeriodStart(e.target.value)} />
                        <p className="form-hint">Leave empty for auto-calculation</p>
                      </div>
                      <div className="form-group">
                        <label>Period End</label>
                        <input type="date" value={generatePeriodEnd} onChange={(e) => setGeneratePeriodEnd(e.target.value)} />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={!generateClientId}>
                      <Receipt size={16} /> Generate Invoice
                    </button>
                  </div>
                </form>
              )}

              {/* Edit Invoice Form */}
              {modalMode === 'editInvoice' && (
                <form onSubmit={handleEditInvoice}>
                  <div className="modal-body">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Invoice Number</label>
                        <input type="text" value={editInvoiceData.invoice_no || ''} onChange={(e) => setEditInvoiceData(prev => ({ ...prev, invoice_no: e.target.value }))} />
                      </div>
                      <div className="form-group">
                        <label>Issue Date</label>
                        <input type="date" value={editInvoiceData.issue_date} onChange={(e) => setEditInvoiceData(prev => ({ ...prev, issue_date: e.target.value }))} />
                      </div>
                      <div className="form-group">
                        <label>Due Date</label>
                        <input type="date" value={editInvoiceData.due_date} onChange={(e) => setEditInvoiceData(prev => ({ ...prev, due_date: e.target.value }))} />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Period Start</label>
                        <input type="date" value={editInvoiceData.period_start} onChange={(e) => setEditInvoiceData(prev => ({ ...prev, period_start: e.target.value }))} />
                      </div>
                      <div className="form-group">
                        <label>Period End</label>
                        <input type="date" value={editInvoiceData.period_end} onChange={(e) => setEditInvoiceData(prev => ({ ...prev, period_end: e.target.value }))} />
                      </div>
                      <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => {
                          const items = currentInvoice?.line_items || []
                          const datesFrom = items.map(i => i.date_from).filter(Boolean).sort()
                          const datesTo = items.map(i => i.date_to).filter(Boolean).sort()
                          if (datesFrom.length && datesTo.length) {
                            setEditInvoiceData(prev => ({ ...prev, period_start: datesFrom[0], period_end: datesTo[datesTo.length - 1] }))
                          }
                        }}>Sync from Line Items</button>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Exchange Rate (PKR)</label>
                      <input type="number" step="0.01" value={editInvoiceData.exchange_rate} onChange={(e) => setEditInvoiceData(prev => ({ ...prev, exchange_rate: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label>Notes</label>
                      <textarea value={editInvoiceData.notes} onChange={(e) => setEditInvoiceData(prev => ({ ...prev, notes: e.target.value }))} rows={3} placeholder="Internal notes..." />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary"><CheckCircle size={16} /> Update Invoice</button>
                  </div>
                </form>
              )}

              {/* Add Line Item Form */}
              {modalMode === 'addLineItem' && (
                <form onSubmit={handleAddLineItem}>
                  <div className="modal-body">
                    <div className="form-group">
                      <label>Description</label>
                      <input type="text" value={lineItemData.description} onChange={(e) => setLineItemData(prev => ({ ...prev, description: e.target.value }))} placeholder="Bot Deployment Services" />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Campaign</label>
                        <input type="text" value={lineItemData.campaign} onChange={(e) => setLineItemData(prev => ({ ...prev, campaign: e.target.value }))} placeholder="Campaign name" />
                      </div>
                      <div className="form-group">
                        <label>Quantity (Bots)</label>
                        <input type="number" value={lineItemData.qty} onChange={(e) => {
                          const qty = parseInt(e.target.value) || 0;
                          setLineItemData(prev => ({ ...prev, qty, amount_usd: parseFloat(((qty * (prev.rate || 0) * (prev.days || 0)) / 30).toFixed(2)) }));
                        }} />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Rate ($/bot)</label>
                        <input type="number" step="0.01" value={lineItemData.rate} onChange={(e) => {
                          const rate = parseFloat(e.target.value) || 0;
                          setLineItemData(prev => ({ ...prev, rate, amount_usd: parseFloat((((prev.qty || 0) * rate * (prev.days || 0)) / 30).toFixed(2)) }));
                        }} />
                      </div>
                      <div className="form-group">
                        <label>Days</label>
                        <input type="number" value={lineItemData.days} onChange={(e) => {
                          const days = parseInt(e.target.value) || 0;
                          setLineItemData(prev => ({ ...prev, days, amount_usd: parseFloat((((prev.qty || 0) * (prev.rate || 0) * days) / 30).toFixed(2)) }));
                        }} />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Date From</label>
                        <input type="date" value={lineItemData.date_from} onChange={(e) => setLineItemData(prev => ({ ...prev, date_from: e.target.value }))} />
                      </div>
                      <div className="form-group">
                        <label>Date To</label>
                        <input type="date" value={lineItemData.date_to} onChange={(e) => setLineItemData(prev => ({ ...prev, date_to: e.target.value }))} />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Amount (USD)</label>
                      <input type="number" step="0.01" value={lineItemData.amount_usd} onChange={(e) => setLineItemData(prev => ({ ...prev, amount_usd: parseFloat(e.target.value) || 0 }))} />
                      <p className="form-hint">
                        Calculated: ${((lineItemData.qty || 0) * (lineItemData.rate || 0) * (lineItemData.days || 0) / 30).toFixed(2)} (qty x rate x days / 30)
                      </p>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary"><Plus size={16} /> Add Line Item</button>
                  </div>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== NOTES MODAL ===== */}
      <AnimatePresence>
        {showNotesModal && notesClient && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowNotesModal(false)}>
            <motion.div className="modal" style={{ maxWidth: '640px' }} initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Notes & Activity - {notesClient.name}</h2>
                <button className="modal-close" onClick={() => setShowNotesModal(false)}><X size={20} /></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '500px', overflow: 'auto' }}>
                {/* Add Note */}
                <div className="note-add-form">
                  <div className="form-row">
                    <div className="form-group" style={{ flex: 1 }}>
                      <select value={newNoteType} onChange={(e) => setNewNoteType(e.target.value)} style={{ padding: '8px 12px' }}>
                        <option value="note">Note</option>
                        <option value="call">Call</option>
                        <option value="email">Email</option>
                        <option value="whatsapp">WhatsApp</option>
                        <option value="meeting">Meeting</option>
                        <option value="follow_up">Follow-up</option>
                      </select>
                    </div>
                  </div>
                  <div className="note-input-row">
                    <textarea
                      value={newNoteContent} onChange={(e) => setNewNoteContent(e.target.value)}
                      placeholder="Add a note..." rows={2}
                      style={{ flex: 1, padding: '10px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.875rem', resize: 'vertical' }}
                    />
                    <button className="btn btn-primary btn-sm" onClick={handleAddNote} disabled={!newNoteContent.trim()} style={{ alignSelf: 'flex-end' }}>
                      <Send size={14} /> Add
                    </button>
                  </div>
                </div>

                {/* Notes List */}
                {notesLoading ? (
                  <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)' }}>Loading...</div>
                ) : clientNotes.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)' }}>No notes yet</div>
                ) : (
                  <div className="notes-list">
                    {clientNotes.map(note => (
                      <div key={note.id} className="note-item">
                        <div className="note-item-header">
                          <span className={`note-type-badge ${note.type}`}>
                            {note.type === 'call' && <Phone size={12} />}
                            {note.type === 'email' && <Mail size={12} />}
                            {note.type === 'whatsapp' && <MessageCircle size={12} />}
                            {note.type === 'meeting' && <Users size={12} />}
                            {note.type === 'follow_up' && <Calendar size={12} />}
                            {note.type === 'note' && <StickyNote size={12} />}
                            {note.type === 'system' && <Activity size={12} />}
                            {note.type}
                          </span>
                          <span className="note-meta">
                            {note.created_by_name && <span className="note-author">{note.created_by_name}</span>}
                            <span className="note-date">{new Date(note.created_at).toLocaleString()}</span>
                          </span>
                          <button className="note-delete" onClick={() => handleDeleteNote(note.id)} title="Delete"><X size={12} /></button>
                        </div>
                        <div className="note-content">{note.content}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== BAD DEBT MODAL ===== */}
      <AnimatePresence>
        {showBadDebtModal && badDebtInvoice && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowBadDebtModal(false)}>
            <motion.div className="modal" style={{ maxWidth: '480px' }} initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Mark as Bad Debt</h2>
                <button className="modal-close" onClick={() => setShowBadDebtModal(false)}><X size={20} /></button>
              </div>
              <div className="modal-body">
                <div className="current-balance" style={{ marginBottom: '20px' }}>
                  <span>{badDebtInvoice.invoice_no}</span>
                  <strong>{formatCurrency(badDebtInvoice.subtotal_usd)}</strong>
                </div>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '16px' }}>
                  This will flag the invoice as uncollectible. The client's risk status will be updated to "Bad Debt".
                </p>
                <div className="form-group">
                  <label>Reason</label>
                  <textarea value={badDebtReason} onChange={(e) => setBadDebtReason(e.target.value)} rows={3} placeholder="Why is this flagged as bad debt?" />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowBadDebtModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleMarkBadDebt} style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}>
                  <ShieldAlert size={16} /> Mark Bad Debt
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== SUBSCRIPTION MODAL ===== */}
      <AnimatePresence>
        {subClient && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSubClient(null)}>
            <motion.div className="modal" style={{ maxWidth: '460px' }} initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Subscription - {subClient.name}</h2>
                <button className="modal-close" onClick={() => setSubClient(null)}><X size={20} /></button>
              </div>
              <div className="modal-body">
                {/* Remaining days */}
                <div className="current-balance" style={{ marginBottom: '20px' }}>
                  <span>Remaining</span>
                  <strong>
                    {subClient.paid_until == null ? (
                      'No expiry'
                    ) : Number(subClient.remaining_days) === 0 ? (
                      <span style={{ color: '#ef4444' }}>Expired</span>
                    ) : (
                      `${subClient.remaining_days} day${Number(subClient.remaining_days) === 1 ? '' : 's'}`
                    )}
                  </strong>
                </div>

                {/* Add days */}
                <div className="form-group">
                  <label>Add days</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="number" min="1" step="1"
                      value={subAddDays}
                      onChange={(e) => setSubAddDays(e.target.value)}
                      placeholder="30"
                      style={{ flex: 1 }}
                    />
                    <button className="btn btn-primary btn-sm" onClick={handleAddDays} disabled={subBusy}>
                      <Plus size={14} /> Add days
                    </button>
                  </div>
                </div>

                {/* Stop bots on expiry */}
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!subClient.stop_bots_on_expiry}
                      disabled={subBusy}
                      onChange={(e) => handleToggleStopOnExpiry(e.target.checked)}
                      style={{ width: '15px', height: '15px', cursor: 'pointer' }}
                    />
                    Stop bots on expiry
                  </label>
                  <p className="form-hint">Default off — bots keep running at expiry unless this is on.</p>
                </div>

                {/* Bot controls */}
                <div className="form-group">
                  <label>Bots</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleClientBots('start')} disabled={subBusy}>
                      <Play size={14} /> Start bots
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleClientBots('stop')} disabled={subBusy} style={{ color: '#ef4444' }}>
                      <Pause size={14} /> Stop bots
                    </button>
                  </div>
                  {!!subClient.bots_auto_stopped && (
                    <p className="form-hint warning">Bots were auto-stopped at expiry.</p>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setSubClient(null)}>Close</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== SAMPLE VOICES MODAL ===== */}
      <AnimatePresence>
        {svClient && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSvClient(null)}>
            <motion.div className="modal" style={{ maxWidth: '640px' }} initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2><Music size={18} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Sample Voices - {svClient.name}</h2>
                <button className="modal-close" onClick={() => setSvClient(null)}><X size={20} /></button>
              </div>
              <div className="modal-body">
                <p className="form-hint" style={{ marginBottom: '16px' }}>
                  Pick the sample recordings this client can play on their portal "Sample Recordings" section.
                  Choose a voice set, tick the clips you want, then Save.
                </p>

                {/* Voice set picker */}
                <div className="form-group">
                  <label>Voice set</label>
                  <select value={svSelectedVsId} onChange={(e) => handleSelectVoiceSet(e.target.value)}>
                    <option value="">Select a voice set…</option>
                    {svVoiceSets.map(vs => (
                      <option key={vs.id} value={vs.id}>
                        {vs.name}{vs.gender ? ` (${vs.gender})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Clips of the chosen voice set */}
                {svSelectedVsId && (
                  <div className="form-group">
                    <label>Clips</label>
                    {svClipsLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-muted)', padding: '8px 0' }}>
                        <Loader2 size={16} className="spin" /> Loading clips…
                      </div>
                    ) : svClips.length === 0 ? (
                      <p className="form-hint">No clips in this voice set.</p>
                    ) : (
                      <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: 'var(--radius-md)' }}>
                        {svClips.map((clip, idx) => {
                          const selected = svSelection.some(v => svKey(v) === `${Number(svSelectedVsId)}::${clip.filename}`)
                          return (
                            <div key={`${clip.filename}-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderBottom: idx < svClips.length - 1 ? '1px solid var(--border-color, rgba(255,255,255,0.06))' : 'none' }}>
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleSampleClip(clip)}
                                style={{ width: '15px', height: '15px', cursor: 'pointer', flexShrink: 0 }}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{clip.category_name}</div>
                                <div className="form-hint" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{clip.filename}</div>
                              </div>
                              <audio controls preload="none" style={{ height: '32px', flexShrink: 0 }} src={`${window.location.origin}${clip.audio_url}`} />
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Current working selection */}
                <div className="form-group">
                  <label>Selected ({svSelection.length})</label>
                  {svSelection.length === 0 ? (
                    <p className="form-hint">Nothing selected yet.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {svSelection.map((item) => (
                        <div key={svKey(item)} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: 'var(--bg-secondary, rgba(255,255,255,0.03))', borderRadius: 'var(--radius-md)' }}>
                          <Volume2 size={16} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label || item.category_name}</div>
                            <div className="form-hint" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.voice_set_name} · {item.filename}</div>
                          </div>
                          <audio controls preload="none" style={{ height: '32px', flexShrink: 0 }} src={`${window.location.origin}${item.audio_url}`} />
                          <button className="btn btn-secondary btn-sm" onClick={() => removeSampleVoice(item)} style={{ color: '#ef4444', flexShrink: 0 }}>
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setSvClient(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSaveSampleVoices} disabled={svBusy}>
                  {svBusy ? <Loader2 size={14} className="spin" /> : <CheckCircle size={14} />} Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invoice Editor */}
      {showInvoiceEditor && editingInvoice && (
        <InvoiceEditor
          invoice={editingInvoice}
          onClose={() => { setShowInvoiceEditor(false); setEditingInvoice(null) }}
          onSave={() => { setShowInvoiceEditor(false); setEditingInvoice(null); loadInvoices() }}
        />
      )}

      {/* Notification backdrop removed — click-outside is handled by a
          document listener above so other toolbar buttons stay clickable */}
    </div>
  )
}

export default Clients
